import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, FileText, HelpCircle, MessageSquare, Settings,
  Plus, Trash2, Edit, Upload, LogOut, Eye, X, Save, Globe2,
  Users, UserPlus, FolderOpen, ClipboardList, BarChart3, Cloud, Newspaper, TrendingUp, Calendar,
  RotateCcw, ChevronDown, ChevronUp, CheckSquare, Square, BookOpen, GraduationCap, Award, ArrowRight, CheckCircle2, XCircle, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { useAuth, getToken, getCachedUser, clearSession } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// API call that ALWAYS reads token from localStorage (not React state)
function api(method, url, data) {
  const token = getToken();
  return axios({ method, url: `${API}${url}`, data, withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

const CATEGORY_COLORS = {
  abituriyent: { primary: '#0077B6', light: '#0077B6', bg: 'bg-[#0077B6]/5', border: 'border-[#0077B6]/30', activeBg: 'bg-[#0077B6]', hoverBg: 'hover:bg-[#005f92]', text: 'text-[#0077B6]' },
  miq: { primary: '#D32F2F', light: '#D32F2F', bg: 'bg-[#D32F2F]/5', border: 'border-[#D32F2F]/30', activeBg: 'bg-[#D32F2F]', hoverBg: 'hover:bg-[#B71C1C]', text: 'text-[#D32F2F]' },
  sertifikasiya: { primary: '#2E7D32', light: '#2E7D32', bg: 'bg-[#2E7D32]/5', border: 'border-[#2E7D32]/30', activeBg: 'bg-[#2E7D32]', hoverBg: 'hover:bg-[#1B5E20]', text: 'text-[#2E7D32]' },
};


export default function AdminDashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeCategory, setActiveCategory] = useState(null); // 'abituriyent' | 'miq' | 'sertifikasiya'
  const [subTab, setSubTab] = useState('students');
  const [stats, setStats] = useState({});
  const [resources, setResources] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [showDialog, setShowDialog] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categoryStats, setCategoryStats] = useState({ abituriyent: {}, miq: {}, sertifikasiya: {} });

  const handleDelete = async (type, id) => {
    try {
      await api('delete', `/api/${type}/${id}`);
      fetchAll();
    } catch (err) {
      console.error('Delete error:', err);
      if (err.response?.status === 401) return; // interceptor handles refresh
      alert('Silmə zamanı xəta: ' + (err.response?.data?.detail || err.message));
    }
    setDeleteConfirm(null);
  };

  const fetchAll = useCallback(async () => {
    try {
      const pc = activeCategory || 'abituriyent';
      const pcq = `?prep_category=${pc}`;
      const [s, r, q, c, st, g, a, at, n, an] = await Promise.all([
        api('get', `/api/admin/stats${pcq}`),
        api('get', `/api/resources${pcq}`),
        api('get', `/api/quizzes${pcq}`),
        api('get', '/api/contact'),
        api('get', `/api/students${pcq}`),
        api('get', `/api/groups${pcq}`),
        api('get', `/api/assignments${pcq}`),
        api('get', `/api/admin/attempts${pcq}`),
        api('get', '/api/news?published_only=false'),
        api('get', `/api/admin/analytics${pcq}`),
      ]);
      setStats(s.data); setResources(r.data); setQuizzes(q.data); setContacts(c.data);
      setStudents(st.data); setGroups(g.data); setAssignments(a.data); setAttempts(at.data);
      setNewsItems(n.data); setAnalytics(an.data);
      // Fetch all category stats for dashboard
      if (!activeCategory || activeCategory === 'abituriyent') {
        try {
          const [sa, sm, ss] = await Promise.all([
            api('get', '/api/admin/stats?prep_category=abituriyent'),
            api('get', '/api/admin/stats?prep_category=miq'),
            api('get', '/api/admin/stats?prep_category=sertifikasiya'),
          ]);
          setCategoryStats({ abituriyent: sa.data, miq: sm.data, sertifikasiya: ss.data });
        } catch {}
      }
    } catch (err) {
      if (err.response?.status !== 401) console.error(err);
    }
  }, [navigate, activeCategory]);

  useEffect(() => {
    if (authLoading) return;
    // Check both React state and localStorage
    const cachedUser = getCachedUser();
    const currentUser = user || cachedUser;
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/admin');
      return;
    }
    fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  const handleLogout = async () => { await logout(); navigate('/admin'); };

  // Upload file
  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${getToken()}` }
      });
      return data.path;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Resource CRUD
  const saveResource = async (data) => {
    if (editItem?.id) {
      await api('put', `/api/resources/${editItem.id}`, data);
    } else {
      await api('post', '/api/resources', data);
    }
    setShowDialog(null);
    setEditItem(null);
    fetchAll();
  };

  const deleteResource = async (id) => {
    setDeleteConfirm({type: 'resources', id, name: 'Resurs'});
  };

  // Quiz CRUD
  const saveQuiz = async (data) => {
    const payload = { ...data, prep_category: activeCategory || 'abituriyent' };
    if (editItem?.id) {
      await api('put', `/api/quizzes/${editItem.id}`, payload);
    } else {
      await api('post', '/api/quizzes', payload);
    }
    setShowDialog(null);
    setEditItem(null);
    fetchAll();
  };

  const deleteQuiz = async (id) => {
    setDeleteConfirm({type: 'quizzes', id, name: 'Test'});
  };

  const [leftTab, setLeftTab] = useState('admin'); // 'admin' = right tabs shown, 'homepage' = homepage editor

  // Built-in page editors
  const builtInPages = {
    homepage: { label: 'Əsas Səhifə', icon: 'Globe2' },
    aboutpage: { label: 'Haqqımızda', icon: 'Users' },
    servicespage: { label: 'Xidmətlər', icon: 'FileText' },
    newspage: { label: 'Yeniliklər', icon: 'Newspaper' },
    contactpage: { label: 'Əlaqə', icon: 'MessageSquare' },
    headerpage: { label: 'Header', icon: 'LayoutDashboard' },
    footerpage: { label: 'Footer', icon: 'Settings' },
  };

  const [menuConfig, setMenuConfig] = useState(null);
  useEffect(() => {
    api('get', '/api/site-content').then(r => {
      const mc = r.data?.menu_config;
      if (mc && mc.items && mc.items.length > 0) setMenuConfig(mc);
      else setMenuConfig({ items: [
        { id: 'homepage', label: 'Əsas Səhifə', icon: 'Globe2', active: true, order: 0 },
        { id: 'aboutpage', label: 'Haqqımızda', icon: 'Users', active: true, order: 1 },
        { id: 'servicespage', label: 'Xidmətlər', icon: 'FileText', active: true, order: 2 },
        { id: 'newspage', label: 'Yeniliklər', icon: 'Newspaper', active: true, order: 3 },
        { id: 'contactpage', label: 'Əlaqə', icon: 'MessageSquare', active: true, order: 4 },
        { id: 'headerpage', label: 'Header', icon: 'LayoutDashboard', active: true, order: 5 },
        { id: 'footerpage', label: 'Footer', icon: 'Settings', active: true, order: 6 },
      ]});
    }).catch(() => {});
  }, []);

  const iconLookup = { Globe2, Users, FileText, Newspaper, MessageSquare, LayoutDashboard, Settings, HelpCircle, BarChart3, ClipboardList, TrendingUp, Cloud };
  const leftMenuItems = (menuConfig?.items || []).filter(m => m.active !== false && m.id !== 'headerpage' && m.id !== 'footerpage').sort((a, b) => (a.order || 0) - (b.order || 0));

  const sideItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Panel' },
    { id: 'contacts', icon: MessageSquare, label: 'Mesajlar' },
    { id: 'abituriyent', icon: GraduationCap, label: 'Abituriyent hazırlığı', isCategory: true },
    { id: 'miq', icon: BookOpen, label: 'MİQ hazırlığı', isCategory: true },
    { id: 'sertifikasiya', icon: Award, label: 'Sertifikasiya hazırlığı', isCategory: true },
  ];

  const getCategorySubTabs = (catId) => [
    { id: 'students', icon: Users, label: catId === 'abituriyent' ? 'Şagirdlər' : 'Tələbələr' },
    { id: 'quizzes', icon: HelpCircle, label: 'Testlər' },
    { id: 'assignments', icon: ClipboardList, label: 'Tapşırıqlar' },
    { id: 'results', icon: BarChart3, label: 'Nəticələr' },
    { id: 'analytics', icon: TrendingUp, label: 'Analitika' },
    { id: 'resources', icon: FileText, label: 'Resurslar' },
  ];

  const handleTabClick = (item) => {
    if (item.isCategory) {
      setActiveTab('category');
      setActiveCategory(item.id);
      setSubTab('students');
    } else {
      setActiveTab(item.id);
      setActiveCategory(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F4F7F6] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F6] flex" data-testid="admin-dashboard">
      {/* LEFT SIDEBAR - Page Tabs */}
      <aside className="w-56 bg-[#03045E] text-white flex-shrink-0 hidden md:flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="earth-globe-wrapper"><div className="earth-globe-tilt"><div className="earth-globe-sphere w-7 h-7" style={{backgroundImage: `url(${API}/api/static/uploads/world_map.jpg)`}} /></div></div>
            <span className="font-bold text-sm">Admin Panel</span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Səhifə Redaktorları */}
          <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/30">Səhifələr</p>
          <button onClick={() => setLeftTab('admin')} data-testid="left-admin"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${leftTab === 'admin' ? 'bg-white/15 text-white shadow-lg shadow-white/5' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
            <LayoutDashboard className="w-[18px] h-[18px]" /> İdarəetmə
          </button>
          {leftMenuItems.map((item, idx) => {
            const Icon = iconLookup[item.icon] || Globe2;
            const sidebarAccents = ['#00B4D8','#48CAE4','#F59E0B','#A78BFA','#F472B6','#34D399','#60A5FA','#FB923C','#818CF8'];
            const accent = sidebarAccents[idx % sidebarAccents.length];
            const isActive = leftTab === item.id;
            return (
              <button key={item.id} onClick={() => setLeftTab(item.id)} data-testid={`left-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${isActive ? 'text-white shadow-lg' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                style={isActive ? { backgroundColor: accent + '25', boxShadow: `0 0 12px ${accent}15` } : {}}>
                <Icon className="w-[18px] h-[18px]" style={isActive ? { color: accent } : {}} /> {item.label}
              </button>
            );
          })}

          {/* Görünüş */}
          <p className="px-4 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/30">Görünüş</p>
          {[
            { id: 'headerpage', label: 'Header', icon: LayoutDashboard, accent: '#60A5FA' },
            { id: 'footerpage', label: 'Footer', icon: Settings, accent: '#FB923C' },
            { id: 'legalpages', label: 'Hüquqi Səhifələr', icon: FileText, accent: '#F59E0B' },
          ].map(item => {
            const isActive = leftTab === item.id;
            return (
              <button key={item.id} onClick={() => setLeftTab(item.id)} data-testid={`left-${item.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ${isActive ? 'text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                style={isActive ? { backgroundColor: item.accent + '25' } : {}}>
                <item.icon className="w-[18px] h-[18px]" style={isActive ? { color: item.accent } : {}} /> {item.label}
              </button>
            );
          })}

          {/* Ayarlar — ayrı, fərqli stil */}
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/30">Ayarlar</p>
            <button onClick={() => setLeftTab('menumanager')} data-testid="left-menumanager"
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${leftTab === 'menumanager' ? 'text-white bg-gradient-to-r from-[#818CF8]/20 to-[#A78BFA]/20 border border-[#818CF8]/30' : 'text-white/50 hover:text-white hover:bg-white/5'}`}>
              <Settings className="w-[18px] h-[18px]" style={leftTab === 'menumanager' ? { color: '#818CF8' } : {}} /> Menyu İdarəsi
            </button>
          </div>
        </nav>
        <div className="p-3 border-t border-white/10">
          <button data-testid="admin-logout" onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold text-white/60 hover:text-white hover:bg-red-500/20 transition-all duration-300">
            <LogOut className="w-[18px] h-[18px]" /> Çıxış
          </button>
        </div>
      </aside>

      {/* RIGHT SIDE */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Homepage Editor */}
        {leftTab === 'homepage' && (
          <div className="flex-1 overflow-auto">
            <HomepageEditor />
          </div>
        )}

        {leftTab === 'aboutpage' && (
          <div className="flex-1 overflow-auto">
            <AboutPageEditor />
          </div>
        )}

        {leftTab === 'servicespage' && (
          <div className="flex-1 overflow-auto">
            <ServicesPageEditor />
          </div>
        )}

        {leftTab === 'newspage' && (
          <div className="flex-1 overflow-auto">
            <NewsPageFullEditor newsItems={newsItems} setShowDialog={setShowDialog} setEditItem={setEditItem} setDeleteConfirm={setDeleteConfirm} fetchAll={fetchAll} />
          </div>
        )}

        {leftTab === 'contactpage' && (
          <div className="flex-1 overflow-auto">
            <ContactPageEditor />
          </div>
        )}

        {leftTab === 'headerpage' && (
          <div className="flex-1 overflow-auto"><HeaderEditor /></div>
        )}

        {leftTab === 'footerpage' && (
          <div className="flex-1 overflow-auto"><FooterEditor /></div>
        )}

        {leftTab === 'menumanager' && (
          <div className="flex-1 overflow-auto">
            <MenuManager config={menuConfig} setConfig={setMenuConfig} builtInPages={builtInPages} iconLookup={iconLookup} />
          </div>
        )}

        {leftTab === 'legalpages' && (
          <div className="flex-1 overflow-auto">
            <LegalPagesEditor />
          </div>
        )}

        {/* Custom page editors */}
        {leftTab.startsWith('custom_') && (
          <div className="flex-1 overflow-auto">
            <CustomPageEditor pageId={leftTab} pageLabel={leftMenuItems.find(m => m.id === leftTab)?.label || 'Səhifə'} />
          </div>
        )}

        {/* Admin Tabs */}
        {leftTab === 'admin' && (
          <>
            {/* Horizontal Tabs */}
            <div className="bg-white border-b border-[#E2E8F0] px-6 overflow-x-auto flex-shrink-0">
              <div className="flex items-center gap-1 py-2">
                {sideItems.map((item) => {
                  const isActive = item.isCategory ? activeCategory === item.id : activeTab === item.id;
                  const catColor = item.isCategory && CATEGORY_COLORS[item.id];
                  const activeCls = isActive
                    ? (catColor ? `${catColor.activeBg} text-white shadow-md` : 'bg-[#00B4D8] text-white shadow-md')
                    : 'text-[#5C6B7A] hover:bg-[#F4F7F6] hover:text-[#1A2530]';
                  return (
                  <button key={item.id} data-testid={`sidebar-${item.id}`} onClick={() => handleTabClick(item)}
                    className={`flex items-center gap-2.5 px-5 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-300 ${activeCls}`}>
                    <item.icon className="w-[18px] h-[18px]" /> {item.label}
                  </button>
                  );
                })}
              </div>
            </div>
            {/* Category Sub-tabs */}
            {activeTab === 'category' && activeCategory && (
              <div className="bg-[#F8FAFB] border-b border-[#E2E8F0] px-6 overflow-x-auto flex-shrink-0">
                <div className="flex items-center gap-1 py-1.5">
                  {getCategorySubTabs(activeCategory).map((st) => {
                    const cc = CATEGORY_COLORS[activeCategory] || CATEGORY_COLORS.abituriyent;
                    return (
                    <button key={st.id} data-testid={`subtab-${st.id}`} onClick={() => setSubTab(st.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${subTab === st.id ? `${cc.activeBg} text-white` : 'text-[#5C6B7A] hover:bg-white hover:text-[#1A2530]'}`}>
                      <st.icon className="w-3.5 h-3.5" /> {st.label}
                    </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Content Area */}
            <main className="flex-1 p-6 md:p-8 overflow-auto">
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-3xl font-bold text-[#1A2530] mb-2">Panel</h1>
            <p className="text-[#5C6B7A] mb-8">Xoş gəldiniz! Platformanın ümumi vəziyyəti.</p>

            {/* Category Stats Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
              {[
                { id: 'abituriyent', label: 'Abituriyent hazırlığı', icon: GraduationCap, ...CATEGORY_COLORS.abituriyent },
                { id: 'miq', label: 'MİQ hazırlığı', icon: BookOpen, ...CATEGORY_COLORS.miq },
                { id: 'sertifikasiya', label: 'Sertifikasiya hazırlığı', icon: Award, ...CATEGORY_COLORS.sertifikasiya },
              ].map(cat => {
                const cs = categoryStats[cat.id] || {};
                const personLabel = cat.id === 'abituriyent' ? 'Şagird' : 'Tələbə';
                const goTo = (sub) => { setActiveTab('category'); setActiveCategory(cat.id); setSubTab(sub); };
                return (
                  <div key={cat.id} data-testid={`cat-card-${cat.id}`}
                    className={`${cat.bg} rounded-2xl p-5 border ${cat.border} text-left transition-all duration-300`}>
                    <button onClick={() => goTo('students')} className="flex items-center gap-3 mb-4 w-full text-left group">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.primary + '18' }}>
                        <cat.icon className="w-5 h-5" style={{ color: cat.primary }} />
                      </div>
                      <div className="font-bold text-[#1A2530] text-base">{cat.label}</div>
                      <svg className="w-4 h-4 text-[#E2E8F0] group-hover:translate-x-1 transition-transform ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: personLabel, value: cs.students || 0, sub: 'students' },
                        { label: 'Qrup', value: cs.groups || 0, sub: 'students' },
                        { label: 'Test', value: cs.quizzes || 0, sub: 'quizzes' },
                      ].map((item, i) => (
                        <button key={i} onClick={() => goTo(item.sub)} data-testid={`cat-metric-${cat.id}-${item.sub}-${i}`}
                          className="bg-white/70 rounded-xl p-3 text-center hover:bg-white hover:shadow-md transition-all cursor-pointer">
                          <div className="text-2xl font-bold" style={{ color: cat.primary }}>{item.value}</div>
                          <div className="text-xs text-[#5C6B7A] mt-0.5">{item.label}</div>
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      {[
                        { label: 'Tapşırıq', value: cs.assignments || 0, sub: 'assignments' },
                        { label: 'Resurs', value: cs.resources || 0, sub: 'resources' },
                        { label: 'Nəticə', value: cs.quiz_attempts || 0, sub: 'results' },
                      ].map((item, i) => (
                        <button key={i} onClick={() => goTo(item.sub)} data-testid={`cat-metric-${cat.id}-${item.sub}`}
                          className="bg-white/40 rounded-lg px-2 py-1.5 text-center hover:bg-white/80 hover:shadow-sm transition-all cursor-pointer">
                          <div className="text-sm font-bold text-[#1A2530]">{item.value}</div>
                          <div className="text-xs text-[#5C6B7A]">{item.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Messages summary */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button onClick={() => setActiveTab('contacts')} className="bg-gradient-to-br from-[#0077B6]/10 to-[#00B4D8]/5 rounded-2xl p-6 border border-[#E2E8F0] text-left hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
                <MessageSquare className="w-5 h-5 text-[#0077B6] mb-3" />
                <div className="text-3xl font-bold mb-1 text-[#0077B6]">{stats.contacts || 0}</div>
                <div className="text-sm text-[#5C6B7A]">Mesajlar</div>
              </button>
              <button onClick={() => setActiveTab('contacts')} className="bg-gradient-to-br from-red-100 to-red-50 rounded-2xl p-6 border border-[#E2E8F0] text-left hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
                <MessageSquare className="w-5 h-5 text-[#e74c3c] mb-3" />
                <div className="text-3xl font-bold mb-1 text-[#e74c3c]">{stats.unread_contacts || 0}</div>
                <div className="text-sm text-[#5C6B7A]">Oxunmamış</div>
              </button>
            </div>

            {stats.unread_contacts > 0 && (
              <button onClick={() => setActiveTab('contacts')} className="w-full bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4 hover:shadow-md transition-all text-left">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-amber-900">{stats.unread_contacts} oxunmamış mesaj</div>
                  <div className="text-sm text-amber-700">Klikləyin və mesajları oxuyun</div>
                </div>
                <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            )}
          </motion.div>
        )}

        {/* Resources */}
        {(activeTab === 'category' && subTab === 'resources') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#1A2530]">Resurslar</h1>
              <div className="flex gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00B4D8] hover:bg-[#00B4D8]/90 text-white text-sm font-semibold font-body cursor-pointer transition-colors" data-testid="sp-upload-btn">
                  <Cloud className="w-4 h-4" /> SharePoint-ə Əlavə Et
                  <input type="file" className="sr-only" onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const token = getToken();
                      await axios.post(`${API}/api/sharepoint/upload?folder=Sayt/Resurslar`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
                      });
                      alert('SharePoint-ə yükləndi!');
                    } catch (err) { alert('Yükləmə xətası: ' + (err.response?.data?.detail || err.message)); }
                    e.target.value = '';
                  }} />
                </label>
                <Button data-testid="add-resource-btn" onClick={() => { setEditItem(null); setShowDialog('resource'); }} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
                  <Plus className="w-4 h-4 mr-2" /> Resurs Əlavə Et
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {resources.map((r) => (
                <div key={r.id} className="bg-white rounded-xl p-4 border border-[#E2E8F0] flex items-center gap-4" data-testid={`admin-resource-${r.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#0077B6]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#0077B6]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-body font-semibold text-[#1A2530] truncate">{r.title}</div>
                    <div className="font-body text-xs text-[#5C6B7A]">{r.resource_type} | {r.category}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => { setEditItem(r); setShowDialog('resource'); }}>
                      <Edit className="w-4 h-4 text-[#5C6B7A]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteResource(r.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {resources.length === 0 && (
                <p className="text-center py-12 font-body text-[#5C6B7A]">Hələ resurs yoxdur</p>
              )}
            </div>

            {/* SharePoint Folder Manager */}
            <div className="mt-10 pt-8 border-t border-[#E2E8F0]">
              <h3 className="text-xl font-bold text-[#1A2530] mb-4 flex items-center gap-2"><Cloud className="w-5 h-5 text-[#0077B6]" /> SharePoint Qovluqları</h3>
              <SharePointFolderManager prepCategory={activeCategory} />
            </div>
          </motion.div>
        )}

        {/* Quizzes */}
        {(activeTab === 'category' && subTab === 'quizzes') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#1A2530]">Testlər</h1>
              <Button
                data-testid="add-quiz-btn"
                onClick={() => { setEditItem(null); setShowDialog('quiz'); }}
                className="bg-[#0077B6] hover:bg-[#005f92] font-body"
              >
                <Plus className="w-4 h-4 mr-2" /> Yeni Test
              </Button>
            </div>
            <div className="space-y-3">
              {quizzes.map((q) => (
                <div key={q.id} className="bg-white rounded-xl p-4 border border-[#E2E8F0] flex items-center gap-4" data-testid={`admin-quiz-${q.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-5 h-5 text-[#00B4D8]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-body font-semibold text-[#1A2530] truncate">{q.title}</div>
                    <div className="font-body text-xs text-[#5C6B7A]">
                      {q.quiz_type === 'pdf_test' ? `PDF | ${q.question_count || 0} sual` : `${q.questions?.length || 0} sual`} | {q.time_limit} dəq | {q.is_active ? 'Aktiv' : 'Deaktiv'} {q.penalty_rule > 0 ? `| ${q.penalty_rule} səhv=-1` : ''}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="icon" variant="ghost" onClick={() => { setEditItem(q); setShowDialog('quiz'); }}>
                      <Edit className="w-4 h-4 text-[#5C6B7A]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => deleteQuiz(q.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {quizzes.length === 0 && (
                <p className="text-center py-12 font-body text-[#5C6B7A]">Hələ test yoxdur</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Contacts */}
        {activeTab === 'contacts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-3xl font-bold text-[#1A2530] mb-8">Mesajlar</h1>
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} className={`bg-white rounded-xl p-5 border ${c.is_read ? 'border-[#E2E8F0]' : 'border-[#0077B6] border-l-4'}`} data-testid={`contact-msg-${c.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-body font-semibold text-[#1A2530]">{c.name}</div>
                      <div className="font-body text-xs text-[#5C6B7A]">{c.email}</div>
                    </div>
                    <div className="font-body text-xs text-[#5C6B7A]">
                      {(() => { const d=new Date(c.created_at); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })()}
                    </div>
                  </div>
                  <div className="font-body font-medium text-[#1A2530] text-sm mb-1">{c.subject}</div>
                  <p className="font-body text-sm text-[#5C6B7A]">{c.message}</p>
                  {!c.is_read && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 font-body"
                      onClick={async () => {
                        await api('put', `/api/contact/${c.id}/read`, null);
                        fetchAll();
                      }}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Oxundu
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="mt-3 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setDeleteConfirm({type: 'contact', id: c.id, name: c.subject || c.name})}>
                    <Trash2 className="w-3 h-3 mr-1" /> Sil
                  </Button>
                </div>
              ))}
              {contacts.length === 0 && (
                <p className="text-center py-12 font-body text-[#5C6B7A]">Hələ mesaj yoxdur</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Students */}
        {(activeTab === 'category' && subTab === 'students') && (
          <StudentsTab students={students} groups={groups} setEditItem={setEditItem} setShowDialog={setShowDialog} setDeleteConfirm={setDeleteConfirm} fetchAll={fetchAll} prepCategory={activeCategory} />
        )}

        {/* Assignments */}
        {(activeTab === 'category' && subTab === 'assignments') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#1A2530]">Tapşırıq Təyinatları</h1>
              <Button data-testid="add-assignment-btn" onClick={() => setShowDialog('assignment')} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
                <Plus className="w-4 h-4 mr-2" /> Tapşırıq Təyin Et
              </Button>
            </div>
            <div className="space-y-3">
              {assignments.map(a => {
                const quiz = quizzes.find(q => q.id === a.quiz_id);
                return (
                  <div key={a.id} className="bg-white rounded-xl p-5 border border-[#E2E8F0]" data-testid={`admin-assignment-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-body font-semibold text-[#1A2530]">{quiz?.title || a.quiz_id}</div>
                        <div className="font-body text-xs text-[#5C6B7A] mt-1">
                          {a.student_ids?.length || 0} şagird | {a.due_date ? `Son tarix: ${(() => { const d=new Date(a.due_date); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })()}` : 'Son tarix yoxdur'}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" data-testid={`delete-assignment-${a.id}`} onClick={() => setDeleteConfirm({type: 'assignments', id: a.id, name: quiz?.title || 'Tapşırıq'})}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                );
              })}
              {assignments.length === 0 && <p className="text-center py-12 font-body text-[#5C6B7A]">Hələ tapşırıq təyinatı yoxdur</p>}
            </div>
          </motion.div>
        )}

        {/* Results */}
        {(activeTab === 'category' && subTab === 'results') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-3xl font-bold text-[#1A2530] mb-6">{activeCategory === 'abituriyent' ? 'Şagird' : 'Tələbə'} Nəticələri</h1>

            {/* All Results */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-[#1A2530]">Bütün Nəticələr</h2>
              {attempts.length > 0 && (
                <a href={`${API}/api/admin/attempts/export?prep_category=${activeCategory || 'abituriyent'}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-xs text-[#5C6B7A] hover:bg-[#F4F7F6] font-semibold" data-testid="export-results-btn">
                  <FileText className="w-3.5 h-3.5" /> CSV İxrac
                </a>
              )}
            </div>
            <div className="space-y-3">
              {attempts.map(a => (
                <AdminAttemptCard key={a.id} attempt={a} />
              ))}
              {attempts.length === 0 && <p className="text-center py-12 font-body text-[#5C6B7A]">Hələ nəticə yoxdur</p>}
            </div>
          </motion.div>
        )}

        {/* Analytics */}
        {(activeTab === 'category' && subTab === 'analytics') && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 className="text-3xl font-bold text-[#1A2530] mb-8">{activeCategory === 'abituriyent' ? 'Şagird' : 'Tələbə'} Analitikası</h1>
            {analytics.length === 0 ? (
              <p className="text-center py-12 text-[#5C6B7A]">Hələ analitik data yoxdur</p>
            ) : (
              <div className="space-y-4">
                {analytics.map((s, i) => {
                  const groupName = groups.find(g => g.id === s.group_id)?.name;
                  return (
                    <div key={s.id} className="bg-white rounded-xl p-6 border border-[#E2E8F0]" data-testid={`analytics-${s.id}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-sm ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-700' : 'bg-[#0077B6]'}`}>
                            {i + 1}
                          </div>
                          <div>
                            <div className="font-semibold text-[#1A2530]">{s.name}</div>
                            <div className="text-xs text-[#5C6B7A]">{s.email} {groupName ? `| ${groupName}` : ''}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold" style={{color: (CATEGORY_COLORS[activeCategory] || CATEGORY_COLORS.abituriyent).primary}}>{s.avg_percent}%</div>
                          <div className="text-xs text-[#5C6B7A]">{s.total_attempts} test | {s.total_score}/{s.total_possible} bal</div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${s.avg_percent}%`, background: `linear-gradient(90deg, ${(CATEGORY_COLORS[activeCategory] || CATEGORY_COLORS.abituriyent).primary}, ${s.avg_percent > 70 ? '#00B4D8' : s.avg_percent > 40 ? '#F59E0B' : '#EF4444'})` }} />
                      </div>
                      {/* Quiz results */}
                      {Object.keys(s.quiz_results).length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {Object.entries(s.quiz_results).map(([qName, r]) => (
                            <span key={qName} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${r.score / r.total > 0.6 ? 'bg-green-100 text-green-700' : r.score / r.total > 0.3 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {qName}: {r.score}/{r.total}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* Site Content Management */}
      </main>
          </>
        )}
      </div>

      {/* Dialogs */}
      <ResourceDialog open={showDialog === 'resource'} onClose={() => { setShowDialog(null); setEditItem(null); }} onSave={saveResource} item={editItem} onUpload={uploadFile} uploading={uploading} />
      {/* Quiz Dialog */}
      <QuizDialog open={showDialog === 'quiz'} onClose={() => { setShowDialog(null); setEditItem(null); }} onSave={saveQuiz} item={editItem} />
      {/* Student Dialog */}
      <StudentDialog open={showDialog === 'student'} onClose={() => { setShowDialog(null); setEditItem(null); }} item={editItem} groups={groups} onDone={() => { setShowDialog(null); setEditItem(null); fetchAll(); }} prepCategory={activeCategory} />
      {/* Group Dialog */}
      <GroupDialog open={showDialog === 'group'} onClose={() => { setShowDialog(null); setEditItem(null); }} item={editItem} onDone={() => { setShowDialog(null); setEditItem(null); fetchAll(); }} prepCategory={activeCategory} />
      {/* Assignment Dialog */}
      <AssignmentDialog open={showDialog === 'assignment'} onClose={() => setShowDialog(null)} quizzes={quizzes} students={students} groups={groups} onDone={() => { setShowDialog(null); fetchAll(); }} prepCategory={activeCategory} />
      {/* News Dialog */}
      <NewsDialog open={showDialog === 'news'} onClose={() => { setShowDialog(null); setEditItem(null); }} item={editItem} onDone={() => { setShowDialog(null); setEditItem(null); fetchAll(); }} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Silmək istəyirsiniz?</DialogTitle>
            <DialogDescription className="font-body">
              <span className="font-semibold">{deleteConfirm?.name}</span> zibil qutusuna göndəriləcək.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="font-body">Ləğv Et</Button>
            <Button data-testid="confirm-delete-btn" onClick={() => handleDelete(deleteConfirm?.type, deleteConfirm?.id)} className="bg-red-600 hover:bg-red-700 text-white font-body">
              <Trash2 className="w-4 h-4 mr-2" /> Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResourceDialog({ open, onClose, onSave, item, onUpload, uploading }) {
  const [form, setForm] = useState({ title: '', description: '', resource_type: 'pdf', url: '', category: 'general' });

  useEffect(() => {
    if (item) setForm({ title: item.title, description: item.description || '', resource_type: item.resource_type, url: item.url || '', category: item.category || 'general' });
    else setForm({ title: '', description: '', resource_type: 'pdf', url: '', category: 'general' });
  }, [item, open]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const path = await onUpload(file);
    if (path) setForm(f => ({ ...f, url: path }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? 'Resursu Redaktə Et' : 'Yeni Resurs'}</DialogTitle>
          <DialogDescription className="font-body">Resurs məlumatlarını daxil edin</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Başlıq</label>
            <Input data-testid="resource-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="font-body" />
          </div>
          <div>
            <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Təsvir</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="font-body" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Növ</label>
              <select
                data-testid="resource-type-select"
                value={form.resource_type}
                onChange={(e) => setForm({ ...form, resource_type: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body"
              >
                <option value="pdf">PDF</option>
                <option value="video">Video</option>
                <option value="image">Şəkil</option>
              </select>
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Kateqoriya</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body"
              >
                <option value="general">Ümumi</option>
                <option value="fiziki">Fiziki Coğrafiya</option>
                <option value="iqtisadi">İqtisadi Coğrafiya</option>
                <option value="xerite">Xəritəşünaslıq</option>
                <option value="imtahan">İmtahana Hazırlıq</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-body text-sm text-[#5C6B7A] mb-1 block">URL (YouTube linki və ya fayl URL-i)</label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="font-body" placeholder="https://youtube.com/watch?v=..." />
          </div>
          <div>
            <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Və ya Fayl Yüklə</label>
            <label className="border-2 border-dashed border-[#E2E8F0] rounded-xl p-6 text-center hover:border-[#0077B6]/30 transition-colors cursor-pointer block relative">
              <Upload className="w-8 h-8 text-[#5C6B7A] mx-auto mb-2" />
              <input data-testid="resource-file-upload" type="file" onChange={handleFileUpload} className="sr-only" />
              <p className="font-body text-sm text-[#5C6B7A]">{uploading ? 'Yüklənir...' : 'Faylı buraya sürükləyin və ya seçin'}</p>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="font-body">Ləğv Et</Button>
          <Button
            data-testid="save-resource-btn"
            onClick={() => saveResource(form)}
            disabled={!form.title}
            className="bg-[#0077B6] hover:bg-[#005f92] font-body"
          >
            <Save className="w-4 h-4 mr-2" /> Saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  function saveResource(data) {
    onSave(data);
  }
}

function QuizDialog({ open, onClose, onSave, item }) {
  const [form, setForm] = useState({
    title: '', description: '', time_limit: 30, questions: [],
    explanation_type: '', explanation_url: '', explanation_view_limit: 0, is_active: true,
    category: '', difficulty: 'medium', penalty_rule: 3, penalty_deduction: 1,
    quiz_type: 'standard', pdf_url: '', answer_count: 4, question_count: 10, open_questions: [], pdf_item_id: '', answer_keys: {}, explanation_item_id: ''
  });
  const [spFiles, setSpFiles] = useState([]);
  const [showSpPicker, setShowSpPicker] = useState(false); // false | 'pdf' | 'explanation'
  const [spFolder, setSpFolder] = useState('');
  const [spBreadcrumb, setSpBreadcrumb] = useState([]);
  const [spLoading, setSpLoading] = useState(false);

  useEffect(() => {
    if (item) setForm({
      title: item.title, description: item.description || '', time_limit: item.time_limit || 30,
      questions: item.questions || [], explanation_type: item.explanation_type || '',
      explanation_url: item.explanation_url || '', explanation_view_limit: item.explanation_view_limit || 0,
      is_active: item.is_active !== false,
      category: item.category || '', difficulty: item.difficulty || 'medium',
      penalty_rule: item.penalty_rule ?? 3, penalty_deduction: item.penalty_deduction ?? 1,
      quiz_type: item.quiz_type || 'standard',
      pdf_url: item.pdf_url || '', answer_count: item.answer_count || 4,
      question_count: item.question_count || 10, open_questions: item.open_questions || [],
      pdf_item_id: item.pdf_item_id || '', answer_keys: item.answer_keys || {}, explanation_item_id: item.explanation_item_id || ''
    });
    else setForm({ title: '', description: '', time_limit: 30, questions: [], explanation_type: '', explanation_url: '', explanation_view_limit: 0, explanation_item_id: '', is_active: true, category: '', difficulty: 'medium', penalty_rule: 3, penalty_deduction: 1, quiz_type: 'standard', pdf_url: '', answer_count: 4, question_count: 10, open_questions: [], pdf_item_id: '', answer_keys: {} });
  }, [item, open]);

  const loadSpFiles = async (folder) => {
    const target = folder !== undefined ? folder : (spFolder || 'Sayt/Dersimiz Cografiya (İsfəndiyar İsgəndərov)/Resurslar');
    setSpLoading(true);
    try {
      const { data } = await api('get', `/api/sharepoint/files?folder=${encodeURIComponent(target)}`);
      setSpFiles(data); setSpFolder(target);
      const parts = target.split('/');
      setSpBreadcrumb(parts.map((p, i) => ({ name: p, path: parts.slice(0, i + 1).join('/') })));
      setShowSpPicker(true);
    } catch { alert('SharePoint faylları yüklənmədi'); }
    finally { setSpLoading(false); }
  };

  const addQuestion = () => {
    setForm(f => ({
      ...f,
      questions: [...f.questions, { id: `q_${Date.now()}`, text: '', options: ['', '', '', ''], correct_answer: '' }]
    }));
  };

  const updateQuestion = (idx, field, value) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[idx] = { ...qs[idx], [field]: value };
      return { ...f, questions: qs };
    });
  };

  const updateOption = (qIdx, oIdx, value) => {
    setForm(f => {
      const qs = [...f.questions];
      const opts = [...qs[qIdx].options];
      opts[oIdx] = value;
      qs[qIdx] = { ...qs[qIdx], options: opts };
      return { ...f, questions: qs };
    });
  };

  const removeQuestion = (idx) => {
    setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? 'Testi Redaktə Et' : 'Yeni Test'}</DialogTitle>
          <DialogDescription className="font-body">Test məlumatlarını daxil edin.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Quiz Type */}
          <div className="flex gap-2 p-1 bg-[#F4F7F6] rounded-xl">
            <button onClick={() => setForm({...form, quiz_type: 'standard'})} data-testid="quiz-type-standard"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${form.quiz_type === 'standard' ? 'bg-[#0077B6] text-white shadow-sm' : 'text-[#5C6B7A]'}`}>Standart</button>
            <button onClick={() => setForm({...form, quiz_type: 'pdf_test'})} data-testid="quiz-type-pdf"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${form.quiz_type === 'pdf_test' ? 'bg-[#D32F2F] text-white shadow-sm' : 'text-[#5C6B7A]'}`}>PDF</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Başlıq</label>
              <Input data-testid="quiz-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Vaxt (dəqiqə)</label>
              <Input data-testid="quiz-time-input" type="number" value={form.time_limit} onChange={(e) => setForm({ ...form, time_limit: parseInt(e.target.value) || 30 })} className="font-body" />
            </div>
          </div>
          <div>
            <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Təsvir</label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="font-body" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">İzah növü</label>
              <select value={form.explanation_type} onChange={(e) => setForm({ ...form, explanation_type: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body">
                <option value="">Yoxdur</option>
                <option value="video">Video (SharePoint)</option>
                <option value="pdf">PDF (SharePoint)</option>
                <option value="image">Şəkil</option>
                <option value="link">Xarici link</option>
              </select>
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">İzah faylı</label>
              {form.explanation_type === 'link' ? (
                <Input value={form.explanation_url} onChange={(e) => setForm({ ...form, explanation_url: e.target.value })} className="font-body" placeholder="https://..." />
              ) : form.explanation_type ? (
                <div className="flex gap-2">
                  <Input value={form.explanation_url} onChange={(e) => setForm({ ...form, explanation_url: e.target.value })} className="font-body flex-1" placeholder="SharePoint-dən seçin..." readOnly />
                  <Button size="sm" variant="outline" onClick={() => {
                    const ext = form.explanation_type === 'video' ? ['.mp4','.avi','.mov','.mkv'] : form.explanation_type === 'pdf' ? ['.pdf'] : ['.jpg','.png','.jpeg','.gif'];
                    const target = spFolder || 'Sayt/Dersimiz Cografiya (İsfəndiyar İsgəndərov)/Resurslar';
                    setSpLoading(true);
                    api('get', `/api/sharepoint/files?folder=${encodeURIComponent(target)}`).then(({data}) => {
                      setSpFiles(data); setSpFolder(target);
                      const parts = target.split('/');
                      setSpBreadcrumb(parts.map((p, i) => ({ name: p, path: parts.slice(0, i + 1).join('/') })));
                      setShowSpPicker('explanation');
                    }).catch(() => alert('SharePoint faylları yüklənmədi')).finally(() => setSpLoading(false));
                  }} className="whitespace-nowrap">
                    <Cloud className="w-3.5 h-3.5 mr-1" /> Seç
                  </Button>
                </div>
              ) : <Input disabled placeholder="İzah növü seçin" className="font-body" />}
            </div>
          </div>
          {form.explanation_type && form.explanation_type !== 'link' && (
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">İzah baxış limiti (0 = limitsiz)</label>
              <Input type="number" min={0} value={String(form.explanation_view_limit)} onChange={e => setForm({...form, explanation_view_limit: e.target.value === '' ? 0 : parseInt(e.target.value)})} className="font-body w-40" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Kateqoriya</label>
              <Input data-testid="quiz-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="font-body" placeholder="Məs: Fiziki Coğrafiya" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Çətinlik</label>
              <select data-testid="quiz-difficulty-select" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body">
                <option value="easy">Asan</option>
                <option value="medium">Orta</option>
                <option value="hard">Çətin</option>
              </select>
            </div>
          </div>

          {/* Penalty Rule */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Neçə səhv?</label>
              <Input data-testid="quiz-penalty-input" type="number" min={0} value={String(form.penalty_rule)} onChange={(e) => setForm({...form, penalty_rule: e.target.value === '' ? 0 : parseInt(e.target.value)})} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Neçə bal çıxılsın?</label>
              <Input data-testid="quiz-penalty-deduction" type="number" min={0} value={String(form.penalty_deduction)} onChange={(e) => setForm({...form, penalty_deduction: e.target.value === '' ? 0 : parseInt(e.target.value)})} className="font-body" />
            </div>
            <div className="flex items-end pb-2">
              <span className="text-xs text-[#5C6B7A]">{form.penalty_rule > 0 ? `Hər ${form.penalty_rule} səhv = -${form.penalty_deduction} bal` : 'Cərimə yoxdur'}</span>
            </div>
          </div>

          {/* PDF Test Settings */}
          {form.quiz_type === 'pdf_test' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <h4 className="font-semibold text-sm text-amber-900 flex items-center gap-2"><FileText className="w-4 h-4" /> PDF Test Parametrləri</h4>
              <div>
                <label className="font-body text-sm text-[#5C6B7A] mb-1 block">PDF Faylı</label>
                <div className="flex gap-2">
                  <Input data-testid="quiz-pdf-url" value={form.pdf_url} onChange={e => setForm({...form, pdf_url: e.target.value})} placeholder="PDF URL və ya SharePoint-dən seçin" className="font-body flex-1" />
                  <Button size="sm" variant="outline" onClick={() => { setShowSpPicker('pdf'); loadSpFiles(); }} className="whitespace-nowrap" data-testid="quiz-sp-picker-btn">
                    <Cloud className="w-3.5 h-3.5 mr-1" /> SharePoint
                  </Button>
                  <label className="inline-flex items-center gap-1 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-[#F4F7F6]">
                    <Upload className="w-3.5 h-3.5" /> Lokal
                    <input type="file" accept=".pdf" className="sr-only" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const formData = new FormData(); formData.append('file', file);
                      try {
                        const token = getToken();
                        const { data } = await axios.post(`${API}/api/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }});
                        setForm(f => ({...f, pdf_url: `${API}${data.url}`}));
                      } catch { alert('Yükləmə xətası'); }
                      e.target.value = '';
                    }} />
                  </label>
                </div>
                {showSpPicker && (
                  <div className="mt-2 border border-amber-200 rounded-lg bg-white overflow-hidden" data-testid="sp-file-browser">
                    <div className="flex items-center gap-1 px-3 py-2 bg-[#F4F7F6] border-b border-amber-200 text-xs overflow-x-auto">
                      {spBreadcrumb.map((b, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-[#5C6B7A]">/</span>}
                          <button onClick={() => loadSpFiles(b.path)} className={`px-1 rounded whitespace-nowrap ${i === spBreadcrumb.length - 1 ? 'text-[#0077B6] font-semibold' : 'text-[#5C6B7A] hover:text-[#1A2530]'}`}>{b.name}</button>
                        </React.Fragment>
                      ))}
                      <button onClick={() => setShowSpPicker(false)} className="ml-auto text-[#5C6B7A] hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {spLoading ? (
                        <div className="flex items-center justify-center py-6"><div className="w-5 h-5 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>
                      ) : spFiles.length === 0 ? (
                        <p className="text-center py-4 text-xs text-[#5C6B7A]">Boş qovluq</p>
                      ) : (
                        spFiles.map(f => {
                          const isPdf = f.name?.toLowerCase().endsWith('.pdf');
                          const isVideo = /\.(mp4|avi|mov|mkv)$/i.test(f.name || '');
                          const isSelectable = f.type === 'folder' || (showSpPicker === 'pdf' && isPdf) || (showSpPicker === 'explanation' && (isPdf || isVideo));
                          return (
                          <button key={f.id} onClick={() => {
                            if (f.type === 'folder') { loadSpFiles(`${spFolder}/${f.name}`); }
                            else if (isSelectable) {
                              (async () => {
                                try {
                                  const { data } = await api('get', `/api/sharepoint/download/${f.id}`);
                                  if (showSpPicker === 'pdf') {
                                    setForm(p => ({...p, pdf_url: data.download_url, pdf_item_id: f.id}));
                                  } else {
                                    setForm(p => ({...p, explanation_url: data.download_url, explanation_item_id: f.id}));
                                  }
                                  setShowSpPicker(false);
                                } catch { alert('Fayl URL alınmadı'); }
                              })();
                            }
                          }} className={`w-full flex items-center gap-2 px-3 py-2 text-sm border-b border-[#F4F7F6] last:border-0 ${isSelectable ? (f.type === 'folder' ? 'hover:bg-[#0077B6]/5' : 'hover:bg-amber-50 cursor-pointer') : 'opacity-40 cursor-not-allowed'}`}>
                            {f.type === 'folder' ? <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" /> : <FileText className="w-4 h-4 text-[#0077B6] flex-shrink-0" />}
                            <span className="truncate text-left flex-1">{f.name}</span>
                            {f.type === 'folder' && <ArrowRight className="w-3 h-3 text-[#5C6B7A]" />}
                            {isPdf && <span className="text-[10px] text-emerald-600 font-semibold">PDF</span>}
                            {isVideo && <span className="text-[10px] text-purple-600 font-semibold">VİDEO</span>}
                          </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-body text-xs text-[#5C6B7A] mb-1 block">Sual sayı</label>
                  <Input data-testid="quiz-question-count" type="number" min={1} value={form.question_count} onChange={e => setForm({...form, question_count: parseInt(e.target.value) || 1})} className="font-body" />
                </div>
                <div>
                  <label className="font-body text-xs text-[#5C6B7A] mb-1 block">Cavab sayı</label>
                  <select data-testid="quiz-answer-count" value={form.answer_count} onChange={e => setForm({...form, answer_count: parseInt(e.target.value)})}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body">
                    <option value={4}>4 (A-D)</option>
                    <option value={5}>5 (A-E)</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-xs text-[#5C6B7A] mb-1 block">Açıq suallar</label>
                  <Input data-testid="quiz-open-questions" 
                    defaultValue={(form.open_questions || []).join(', ')}
                    key={`oq-${open}-${item?.id || 'new'}`}
                    onBlur={e => {
                      const nums = e.target.value.split(/[,;\s]+/).map(s => parseInt(s.trim())).filter(n => !isNaN(n) && n > 0);
                      setForm(f => ({...f, open_questions: nums}));
                    }}
                    className="font-body" placeholder="1, 5, 10" />
                </div>
              </div>
              <p className="text-xs text-amber-700">Açıq suallar: vergüllə ayırın (məs: 1, 5, 10). Bu suallarda tələbə cavabını yazacaq.</p>

              {/* Answer Keys */}
              <div>
                <h4 className="font-semibold text-sm text-amber-900 mb-2">Düzgün Cavablar</h4>
                <div className="grid grid-cols-5 gap-2 max-h-60 overflow-y-auto">
                  {Array.from({ length: form.question_count || 0 }, (_, i) => {
                    const qid = `q_${i + 1}`;
                    const isOpen = (form.open_questions || []).includes(i + 1);
                    return (
                      <div key={i} className="flex items-center gap-1">
                        <span className="text-xs font-bold text-[#1A2530] w-6">{i + 1}.</span>
                        {isOpen ? (
                          <Input value={form.answer_keys[qid] || ''} onChange={e => setForm(f => ({...f, answer_keys: {...f.answer_keys, [qid]: e.target.value}}))}
                            className="h-7 text-xs px-1" placeholder="Cavab" />
                        ) : (
                          <select value={form.answer_keys[qid] || ''} onChange={e => setForm(f => ({...f, answer_keys: {...f.answer_keys, [qid]: e.target.value}}))}
                            className="h-7 rounded border border-input bg-background px-1 text-xs flex-1">
                            <option value="">-</option>
                            {Array.from({ length: form.answer_count || 4 }, (_, oi) => (
                              <option key={oi} value={String.fromCharCode(65 + oi)}>{String.fromCharCode(65 + oi)}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            <label className="font-body text-sm text-[#5C6B7A]">Aktiv (tələbələrə göstər)</label>
          </div>

          {/* Standard Questions - only for standard quiz type */}
          {form.quiz_type === 'standard' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="font-body font-semibold text-[#1A2530]">Suallar ({form.questions.length})</label>
              <Button size="sm" variant="outline" onClick={addQuestion} className="font-body" data-testid="add-question-btn">
                <Plus className="w-3 h-3 mr-1" /> Sual Əlavə Et
              </Button>
            </div>
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
              {form.questions.map((q, qi) => (
                <div key={q.id} className="bg-[#F4F7F6] rounded-xl p-4 border border-[#E2E8F0]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-body text-sm font-semibold text-[#1A2530]">Sual {qi + 1}</span>
                    <Button size="icon" variant="ghost" onClick={() => removeQuestion(qi)} className="h-7 w-7">
                      <X className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                  <Input
                    data-testid={`question-text-${qi}`}
                    placeholder="Sual mətni"
                    value={q.text}
                    onChange={(e) => updateQuestion(qi, 'text', e.target.value)}
                    className="mb-3 font-body text-sm"
                  />
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {q.options.map((opt, oi) => (
                      <Input
                        key={oi}
                        data-testid={`question-${qi}-option-${oi}`}
                        placeholder={`Seçim ${String.fromCharCode(65 + oi)}`}
                        value={opt}
                        onChange={(e) => updateOption(qi, oi, e.target.value)}
                        className="font-body text-sm"
                      />
                    ))}
                  </div>
                  <div>
                    <label className="font-body text-xs text-[#5C6B7A] mb-1 block">Düzgün cavab</label>
                    <select
                      data-testid={`question-${qi}-correct`}
                      value={q.correct_answer}
                      onChange={(e) => updateQuestion(qi, 'correct_answer', e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm font-body"
                    >
                      <option value="">Seçin</option>
                      {q.options.filter(o => o).map((opt, oi) => (
                        <option key={oi} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="font-body">Ləğv Et</Button>
          <Button
            data-testid="save-quiz-btn"
            onClick={() => onSave(form)}
            disabled={!form.title}
            className="bg-[#0077B6] hover:bg-[#005f92] font-body"
          >
            <Save className="w-4 h-4 mr-2" /> Saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SiteContentManager() {
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    api('get', '/api/site-content').then(r => setContent(r.data)).catch(console.error);
  }, []);

  const saveSection = async (section, data) => {
    setSaving(section);
    try {
      await api('put', '/api/site-content', { section, content: data });
      setSaved(section);
      setTimeout(() => setSaved(''), 2000);
    } catch (err) { console.error(err); }
    finally { setSaving(''); }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const hero = content.hero || {};
  const about = content.about || {};
  const teacher = content.teacher || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <h1 className="text-3xl font-bold text-[#1A2530] mb-8">Sayt Məzmunu</h1>
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="bg-white rounded-xl p-6 border border-[#E2E8F0]" data-testid="content-hero">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[#1A2530]">Ana Səhifə (Hero)</h3>
            <Button size="sm" onClick={() => saveSection('hero', hero)} disabled={saving === 'hero'} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
              {saving === 'hero' ? '...' : saved === 'hero' ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Başlıq</label>
              <Input value={hero.title || ''} onChange={e => updateField('hero', 'title', e.target.value)} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Alt başlıq</label>
              <Input value={hero.subtitle || ''} onChange={e => updateField('hero', 'subtitle', e.target.value)} className="font-body" />
            </div>
            <div className="md:col-span-2">
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Təsvir</label>
              <Textarea value={hero.description || ''} onChange={e => updateField('hero', 'description', e.target.value)} className="font-body" rows={2} />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-xl p-6 border border-[#E2E8F0]" data-testid="content-about">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[#1A2530]">Haqqımızda</h3>
            <Button size="sm" onClick={() => saveSection('about', about)} disabled={saving === 'about'} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
              {saving === 'about' ? '...' : saved === 'about' ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Təcrübə</label>
              <Input value={about.experience || ''} onChange={e => updateField('about', 'experience', e.target.value)} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Şagird sayı</label>
              <Input value={about.students || ''} onChange={e => updateField('about', 'students', e.target.value)} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Kurs sayı</label>
              <Input value={about.courses || ''} onChange={e => updateField('about', 'courses', e.target.value)} className="font-body" />
            </div>
            <div className="md:col-span-3">
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Təsvir</label>
              <Textarea value={about.description || ''} onChange={e => updateField('about', 'description', e.target.value)} className="font-body" rows={3} />
            </div>
          </div>
        </div>

        {/* Teacher Info */}
        <div className="bg-white rounded-xl p-6 border border-[#E2E8F0]" data-testid="content-teacher">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[#1A2530]">Müəllim Məlumatları</h3>
            <Button size="sm" onClick={() => saveSection('teacher', teacher)} disabled={saving === 'teacher'} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
              {saving === 'teacher' ? '...' : saved === 'teacher' ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Ad</label>
              <Input value={teacher.name || ''} onChange={e => updateField('teacher', 'name', e.target.value)} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Vəzifə</label>
              <Input value={teacher.title || ''} onChange={e => updateField('teacher', 'title', e.target.value)} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Telefon</label>
              <Input value={teacher.phone || ''} onChange={e => updateField('teacher', 'phone', e.target.value)} className="font-body" />
            </div>
            <div>
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">E-poçt</label>
              <Input value={teacher.email || ''} onChange={e => updateField('teacher', 'email', e.target.value)} className="font-body" />
            </div>
            <div className="md:col-span-2">
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Haqqında</label>
              <Textarea value={teacher.bio || ''} onChange={e => updateField('teacher', 'bio', e.target.value)} className="font-body" rows={3} />
            </div>
            <div className="md:col-span-2">
              <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Təhsil</label>
              <Input value={teacher.education || ''} onChange={e => updateField('teacher', 'education', e.target.value)} className="font-body" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}


function StudentDialog({ open, onClose, item, groups, onDone, prepCategory }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', group_id: '' });
  useEffect(() => {
    if (item) setForm({ name: item.name, email: item.email, password: '', group_id: item.group_id || '' });
    else setForm({ name: '', email: '', password: '', group_id: '' });
  }, [item, open]);

  const handleSave = async () => {
    try {
      const payload = { ...form, prep_category: prepCategory || 'abituriyent' };
      if (item?.id) await api('put', `/api/students/${item.id}`, payload);
      else await api('post', '/api/students', payload);
      onDone();
    } catch (err) { alert(err.response?.data?.detail || 'Xəta'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? `${prepCategory === 'abituriyent' ? 'Şagirdi' : 'Tələbəni'} Redaktə Et` : `Yeni ${prepCategory === 'abituriyent' ? 'Şagird' : 'Tələbə'}`}</DialogTitle>
          <DialogDescription className="font-body">{prepCategory === 'abituriyent' ? 'Şagird' : 'Tələbə'} məlumatlarını daxil edin</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><label className="font-body text-sm text-[#5C6B7A] mb-1 block">Ad</label>
            <Input data-testid="student-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div><label className="font-body text-sm text-[#5C6B7A] mb-1 block">E-poçt</label>
            <Input data-testid="student-email-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div><label className="font-body text-sm text-[#5C6B7A] mb-1 block">Parol {item && '(boş buraxsanız dəyişməz)'}</label>
            <Input data-testid="student-password-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} /></div>
          <div><label className="font-body text-sm text-[#5C6B7A] mb-1 block">Qrup</label>
            <select value={form.group_id} onChange={e => setForm({...form, group_id: e.target.value})} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body">
              <option value="">Qrup yoxdur</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="font-body">Ləğv Et</Button>
          <Button data-testid="save-student-btn" onClick={handleSave} disabled={!form.name || !form.email || (!item && !form.password)} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
            <Save className="w-4 h-4 mr-2" /> Saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupDialog({ open, onClose, onDone, item, prepCategory }) {
  const [name, setName] = useState('');
  useEffect(() => { if (open) setName(item?.name || ''); }, [open, item]);
  const handleSave = async () => {
    try {
      if (item) { await api('put', `/api/groups/${item.id}`, { name, description: item.description || '', prep_category: prepCategory || 'abituriyent' }); }
      else { await api('post', '/api/groups', { name, description: '', prep_category: prepCategory || 'abituriyent' }); }
      onDone();
    } catch (err) { alert(err.response?.data?.detail || 'Xəta'); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? 'Qrupu Redaktə Et' : 'Yeni Qrup'}</DialogTitle>
          <DialogDescription className="font-body">Qrup adını daxil edin</DialogDescription>
        </DialogHeader>
        <Input data-testid="group-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Qrup adı" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv Et</Button>
          <Button data-testid="save-group-btn" onClick={handleSave} disabled={!name} className="bg-[#0077B6] hover:bg-[#005f92] font-body"><Save className="w-4 h-4 mr-2" /> Saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignmentDialog({ open, onClose, quizzes, students, groups, onDone, prepCategory }) {
  const [form, setForm] = useState({ quiz_id: '', student_ids: [], group_ids: [], due_date: '' });
  useEffect(() => { if(open) setForm({ quiz_id: '', student_ids: [], group_ids: [], due_date: '' }); }, [open]);

  const toggleStudent = (id) => {
    setForm(f => ({ ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter(s => s !== id) : [...f.student_ids, id] }));
  };
  const toggleGroup = (id) => {
    setForm(f => ({ ...f, group_ids: f.group_ids.includes(id) ? f.group_ids.filter(g => g !== id) : [...f.group_ids, id] }));
  };

  const handleSave = async () => {
    try { await api('post', '/api/assignments', { ...form, prep_category: prepCategory || 'abituriyent' }); onDone(); }
    catch (err) { alert(err.response?.data?.detail || 'Xəta'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Tapşırıq Təyin Et</DialogTitle>
          <DialogDescription className="font-body">Test seçin və şagird/qrup təyin edin</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><label className="font-body text-sm text-[#5C6B7A] mb-1 block">Test seçin</label>
            <select data-testid="assignment-quiz-select" value={form.quiz_id} onChange={e => setForm({...form, quiz_id: e.target.value})} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body">
              <option value="">Seçin</option>
              {quizzes.filter(q => !q.is_deleted).map(q => <option key={q.id} value={q.id}>{q.title}</option>)}
            </select></div>
          <div><label className="font-body text-sm text-[#5C6B7A] mb-1 block">Son tarix (ixtiyari)</label>
            <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
          {groups.length > 0 && (
            <div><label className="font-body text-sm text-[#5C6B7A] mb-2 block">Qruplar</label>
              <div className="flex flex-wrap gap-2">
                {groups.map(g => (
                  <button key={g.id} onClick={() => toggleGroup(g.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold font-body border transition-all ${form.group_ids.includes(g.id) ? 'bg-[#0077B6] text-white border-[#0077B6]' : 'border-[#E2E8F0] text-[#5C6B7A] hover:border-[#0077B6]'}`}>
                    {g.name}
                  </button>
                ))}
              </div></div>
          )}
          <div><label className="font-body text-sm text-[#5C6B7A] mb-2 block">Fərdi şagirdlər</label>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {students.map(s => (
                <label key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#F4F7F6] cursor-pointer">
                  <input type="checkbox" checked={form.student_ids.includes(s.id)} onChange={() => toggleStudent(s.id)} className="rounded" />
                  <span className="font-body text-sm text-[#1A2530]">{s.name} ({s.email})</span>
                </label>
              ))}
            </div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv Et</Button>
          <Button data-testid="save-assignment-btn" onClick={handleSave} disabled={!form.quiz_id || (form.student_ids.length === 0 && form.group_ids.length === 0)} className="bg-[#0077B6] hover:bg-[#005f92] font-body">
            <Save className="w-4 h-4 mr-2" /> Təyin Et
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NewsDialog({ open, onClose, item, onDone }) {
  const [form, setForm] = useState({ title: '', summary: '', content: '', image_url: '', video_url: '', gallery_images: [], pdf_url: '', category: 'yenilik', is_published: true, published_date: '' });
  const [galleryInput, setGalleryInput] = useState('');
  useEffect(() => {
    if (item) setForm({ title: item.title, summary: item.summary || '', content: item.content || '', image_url: item.image_url || '', video_url: item.video_url || '', gallery_images: item.gallery_images || [], pdf_url: item.pdf_url || '', category: item.category || 'yenilik', is_published: item.is_published !== false, published_date: item.published_date || '' });
    else setForm({ title: '', summary: '', content: '', image_url: '', video_url: '', gallery_images: [], pdf_url: '', category: 'yenilik', is_published: true, published_date: '' });
    setGalleryInput('');
  }, [item, open]);

  const addGalleryImage = () => {
    if (galleryInput.trim()) { setForm(f => ({...f, gallery_images: [...f.gallery_images, galleryInput.trim()]})); setGalleryInput(''); }
  };

  const handleSave = async () => {
    try {
      if (item?.id) await api('put', `/api/news/${item.id}`, form);
      else await api('post', '/api/news', form);
      onDone();
    } catch (err) { alert(err.response?.data?.detail || 'Xəta'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? 'Xəbəri Redaktə Et' : 'Yeni Xəbər'}</DialogTitle>
          <DialogDescription>Xəbər, şəkil, video və PDF əlavə edin</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><label className="text-sm text-[#5C6B7A] mb-1 block">Başlıq</label>
            <Input data-testid="news-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} /></div>
          <div><label className="text-sm text-[#5C6B7A] mb-1 block">Qısa Xülasə</label>
            <Textarea value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} rows={2} /></div>
          <div><label className="text-sm text-[#5C6B7A] mb-1 block">Məzmun <span className="text-[#0077B6]">(Markdown dəstəklənir)</span></label>
            <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={6} placeholder="## Başlıq&#10;Mətn **qalın** *kursiv*&#10;- Siyahı" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm text-[#5C6B7A] mb-1 block">Əsas Şəkil</label>
              <div className="flex gap-2">
                <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="flex-1" />
                <label className="inline-flex items-center gap-1 px-3 h-10 rounded-md bg-[#0077B6] hover:bg-[#005f92] text-white text-xs font-medium cursor-pointer transition-colors flex-shrink-0">
                  <Upload className="w-3 h-3" /> Əlavə et
                  <input type="file" accept="image/*" className="sr-only" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    try { const fd = new FormData(); fd.append('file', file); const { data } = await api('post', '/api/upload', fd); setForm(f => ({...f, image_url: `${API}/api/files/${data.path}`})); }
                    catch { alert('Yükləmə xətası'); } e.target.value = '';
                  }} />
                </label>
              </div>
              {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-16 rounded-lg object-cover border" />}
            </div>
            <div><label className="text-sm text-[#5C6B7A] mb-1 block">Video URL (YouTube)</label>
              <Input value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." /></div>
          </div>
          <div><label className="text-sm text-[#5C6B7A] mb-1 block">PDF Sənəd URL</label>
            <Input value={form.pdf_url} onChange={e => setForm({...form, pdf_url: e.target.value})} placeholder="https://... .pdf" /></div>
          {/* Gallery */}
          <div>
            <label className="text-sm text-[#5C6B7A] mb-1 block">Şəkil Qalereyası ({form.gallery_images.length} şəkil)</label>
            <div className="flex gap-2 mb-2">
              <Input value={galleryInput} onChange={e => setGalleryInput(e.target.value)} placeholder="Şəkil URL əlavə edin" className="flex-1" />
              <Button type="button" size="sm" onClick={addGalleryImage} disabled={!galleryInput.trim()} variant="outline">URL əlavə et</Button>
              <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-[#0077B6] hover:bg-[#005f92] text-white text-xs font-medium cursor-pointer transition-colors flex-shrink-0">
                <Upload className="w-3 h-3" /> Şəkil yüklə
                <input type="file" accept="image/*" className="sr-only" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  try {
                    const fd = new FormData(); fd.append('file', file);
                    const { data } = await api('post', '/api/upload', fd);
                    const url = `${API}/api/files/${data.path}`;
                    setForm(f => ({...f, gallery_images: [...f.gallery_images, url]}));
                  } catch { alert('Yükləmə xətası'); }
                  e.target.value = '';
                }} />
              </label>
            </div>
            {form.gallery_images.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.gallery_images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={img} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#E2E8F0]" />
                    <button onClick={() => setForm(f => ({...f, gallery_images: f.gallery_images.filter((_,j)=>j!==i)}))}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="text-sm text-[#5C6B7A] mb-1 block">Kateqoriya</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                <option value="yenilik">Yenilik</option><option value="ders">Dərs Materialı</option><option value="imtahan">İmtahan</option><option value="tedris">Tədris</option><option value="digər">Digər</option>
              </select></div>
            <div><label className="text-sm text-[#5C6B7A] mb-1 block">Tarix (gg.aa.iiii)</label>
              <div className="relative">
                <Input value={form.published_date ? (() => { const p=form.published_date.split('-'); return p.length===3 ? `${p[2]}.${p[1]}.${p[0]}` : form.published_date; })() : ''}
                  onChange={e => {
                    const v = e.target.value.replace(/[^\d.]/g,'');
                    const parts = v.split('.');
                    if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
                      setForm({...form, published_date: `${parts[2]}-${parts[1]}-${parts[0]}`});
                    } else {
                      setForm({...form, published_date: v});
                    }
                  }}
                  placeholder="gg.aa.iiii" className="pr-10" />
                <input type="date" value={form.published_date} onChange={e => setForm({...form, published_date: e.target.value})}
                  className="absolute right-0 top-0 w-10 h-full opacity-0 cursor-pointer" />
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B7A] pointer-events-none" />
              </div>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} className="rounded" />
              <label className="text-sm text-[#5C6B7A]">Yayımla</label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv Et</Button>
          <Button data-testid="save-news-btn" onClick={handleSave} disabled={!form.title} className="bg-[#0077B6] hover:bg-[#005f92]">
            <Save className="w-4 h-4 mr-2" /> Saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SharePointFolderManager({ prepCategory }) {
  const categoryFolderMap = {
    abituriyent: 'Abituriyent hazırlığı',
    miq: 'MİQ hazırlığı',
    sertifikasiya: 'Sertifikasiya hazırlığı',
  };
  const basePath = 'Sayt';
  const catFolder = categoryFolderMap[prepCategory] || '';
  const defaultPath = catFolder ? `${basePath}/Dersimiz Cografiya (İsfəndiyar İsgəndərov)/Resurslar/${catFolder}` : basePath;
  const defaultBreadcrumb = catFolder ? [
    { name: 'Sayt', path: 'Sayt' },
    { name: 'Dersimiz Cografiya (İsfəndiyar İsgəndərov)', path: 'Sayt/Dersimiz Cografiya (İsfəndiyar İsgəndərov)' },
    { name: 'Resurslar', path: 'Sayt/Dersimiz Cografiya (İsfəndiyar İsgəndərov)/Resurslar' },
    { name: catFolder, path: defaultPath },
  ] : [{ name: 'Sayt', path: 'Sayt' }];

  const [files, setFiles] = useState([]);
  const [folder, setFolder] = useState(defaultPath);
  const [breadcrumb, setBreadcrumb] = useState(defaultBreadcrumb);
  const [newFolderName, setNewFolderName] = useState('');
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setFolder(defaultPath);
    setBreadcrumb(defaultBreadcrumb);
  }, [prepCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFiles = useCallback(async () => {
    try { const { data } = await api('get', `/api/sharepoint/files?folder=${encodeURIComponent(folder)}`); setFiles(data); }
    catch { setFiles([]); }
  }, [folder]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const navigateFolder = (f) => {
    const newPath = `${folder}/${f.name}`;
    setFolder(newPath);
    setBreadcrumb(prev => [...prev, { name: f.name, path: newPath }]);
  };

  const navigateBreadcrumb = (i) => {
    setFolder(breadcrumb[i].path);
    setBreadcrumb(prev => prev.slice(0, i + 1));
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreating(true);
    try {
      await api('post', `/api/sharepoint/folder?folder_name=${encodeURIComponent(newFolderName)}&parent=${encodeURIComponent(folder)}`);
      setNewFolderName('');
      fetchFiles();
    } catch (err) { alert('Qovluq yaradıla bilmədi: ' + (err.response?.data?.detail || err.message)); }
    finally { setCreating(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken();
      await axios.post(`${API}/api/sharepoint/upload?folder=${encodeURIComponent(folder)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      fetchFiles();
    } catch (err) { alert('Yükləmə xətası: ' + (err.response?.data?.detail || err.message)); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const [assignFile, setAssignFile] = useState(null);
  const [assignStudents, setAssignStudents] = useState([]);
  const [assignGroups, setAssignGroups] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');

  const openPreview = async (f) => {
    try {
      const { data } = await api('get', `/api/sharepoint/preview/${f.id}`);
      setPreviewUrl(data.embed_url);
      setPreviewName(f.name);
    } catch {
      try {
        const { data } = await api('get', `/api/sharepoint/download/${f.id}`);
        setPreviewUrl(data.download_url);
        setPreviewName(f.name);
      } catch { alert('Önizləmə alınmadı'); }
    }
  };

  // We need students and groups for assignment - fetch from parent
  const [allStudents, setAllStudents] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  useEffect(() => {
    api('get', '/api/students').then(r => setAllStudents(r.data)).catch(() => {});
    api('get', '/api/groups').then(r => setAllGroups(r.data)).catch(() => {});
  }, []);

  const assignToStudents = async () => {
    if (!assignFile) return;
    try {
      await api('post', '/api/sharepoint/assign', { file_id: assignFile.id, file_name: assignFile.name, file_path: `${folder}/${assignFile.name}`, student_ids: assignStudents, group_ids: assignGroups });
      alert('Fayl şagirdlərə təyin olundu!');
      setAssignFile(null); setAssignStudents([]); setAssignGroups([]);
    } catch (err) { alert('Təyin xətası'); }
  };

  return (
    <div data-testid="sp-folder-manager">
      {/* Breadcrumb + Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1 text-sm flex-1">
          {breadcrumb.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-[#5C6B7A] mx-1">/</span>}
              <button onClick={() => navigateBreadcrumb(i)} className={`px-2 py-0.5 rounded ${i === breadcrumb.length - 1 ? 'text-[#0077B6] font-semibold' : 'text-[#5C6B7A] hover:text-[#1A2530]'}`}>
                {b.name}
              </button>
            </React.Fragment>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Yeni qovluq adı" className="w-40 h-9 text-sm" data-testid="sp-new-folder-input" />
          <Button size="sm" onClick={createFolder} disabled={creating || !newFolderName.trim()} className="bg-[#03045E] hover:bg-[#03045E]/90 h-9" data-testid="sp-create-folder-btn">
            <FolderOpen className="w-3.5 h-3.5 mr-1" /> {creating ? '...' : 'Yarat'}
          </Button>
          <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#0077B6] hover:bg-[#005f92] text-white text-sm font-medium cursor-pointer transition-colors">
            <Upload className="w-3.5 h-3.5" /> {uploading ? '...' : 'Əlavə et'}
            <input type="file" className="sr-only" onChange={uploadFile} />
          </label>
        </div>
      </div>

      {/* Files */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {files.map(f => {
          const downloadFile = async (e) => {
            e.stopPropagation();
            try { const { data } = await api('get', `/api/sharepoint/download/${f.id}`); if (data.download_url) window.open(data.download_url, '_blank'); }
            catch { alert('Endirmə xətası'); }
          };
          return (
            <div key={f.id} className={`bg-[#F4F7F6] rounded-xl p-3 border border-[#E2E8F0] hover:border-[#0077B6]/30 transition-all cursor-pointer group ${f.type === 'folder' ? 'hover:bg-[#0077B6]/5' : ''}`}
              onClick={() => f.type === 'folder' ? navigateFolder(f) : (f.name?.toLowerCase().endsWith('.pdf') ? openPreview(f) : downloadFile({stopPropagation:()=>{}}))}>
              <div className="flex items-center gap-2">
                {f.type === 'folder' ? <FolderOpen className="w-5 h-5 text-amber-500" /> : <FileText className="w-5 h-5 text-[#0077B6]" />}
                <span className="text-sm text-[#1A2530] truncate font-medium flex-1">{f.name}</span>
                {f.type !== 'folder' && (
                  <div className="flex gap-1">
                    {f.name?.toLowerCase().endsWith('.pdf') && (
                      <button onClick={(e) => { e.stopPropagation(); openPreview(f); }} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white transition-all" title="PDF Bax" data-testid={`preview-${f.id}`}>
                        <Eye className="w-3.5 h-3.5 text-emerald-600" />
                      </button>
                    )}
                    <button onClick={downloadFile} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white transition-all" title="Endir">
                      <svg className="w-3.5 h-3.5 text-[#0077B6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setAssignFile(f); }} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white transition-all" title="Şagirdlərə təyin et">
                      <Users className="w-3.5 h-3.5 text-[#03045E]" />
                    </button>
                  </div>
                )}
              </div>
              {f.type !== 'folder' && f.size > 0 && <span className="text-[10px] text-[#5C6B7A] mt-1 block">{(f.size / 1024).toFixed(0)} KB</span>}
            </div>
          );
        })}
        {files.length === 0 && <p className="col-span-full text-center py-6 text-sm text-[#5C6B7A]">Bu qovluq boşdur</p>}
      </div>

      {/* Assign File Dialog */}
      <Dialog open={!!assignFile} onOpenChange={() => setAssignFile(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Fayl Şagirdlərə Təyin Et</DialogTitle>
            <DialogDescription>"{assignFile?.name}" faylını şagird və ya qruplara təyin edin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {allGroups.length > 0 && (
              <div><label className="text-sm text-[#5C6B7A] mb-2 block">Qruplar</label>
                <div className="flex flex-wrap gap-2">{allGroups.map(g => (
                  <button key={g.id} onClick={() => setAssignGroups(prev => prev.includes(g.id) ? prev.filter(x=>x!==g.id) : [...prev, g.id])}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${assignGroups.includes(g.id) ? 'bg-[#03045E] text-white border-[#03045E]' : 'border-[#E2E8F0] text-[#5C6B7A]'}`}>{g.name}</button>
                ))}</div></div>
            )}
            <div><label className="text-sm text-[#5C6B7A] mb-2 block">Fərdi şagirdlər</label>
              <div className="max-h-40 overflow-y-auto space-y-1">{allStudents.map(s => (
                <label key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#F4F7F6] cursor-pointer">
                  <input type="checkbox" checked={assignStudents.includes(s.id)} onChange={() => setAssignStudents(prev => prev.includes(s.id) ? prev.filter(x=>x!==s.id) : [...prev, s.id])} />
                  <span className="text-sm">{s.name}</span>
                </label>
              ))}</div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFile(null)}>Ləğv Et</Button>
            <Button onClick={assignToStudents} disabled={assignStudents.length===0 && assignGroups.length===0} className="bg-[#0077B6] hover:bg-[#005f92]">
              <Users className="w-4 h-4 mr-2" /> Təyin Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#E2E8F0] bg-white">
            <span className="font-semibold text-sm text-[#1A2530] truncate">{previewName}</span>
            <Button size="sm" variant="ghost" onClick={() => setPreviewUrl(null)} className="h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
          </div>
          <iframe src={previewUrl} className="w-full flex-1" style={{height: 'calc(85vh - 48px)'}} title="PDF Preview" data-testid="pdf-preview-iframe" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImageUploadField({ label, value, onChange, section }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const { data } = await api('post', '/api/upload', formData);
      const url = `${API}/api/files/${data.path}`;
      onChange(url);
    } catch { alert('Yükləmə xətası'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <div>
      <label className="text-xs text-[#5C6B7A] mb-1 block">{label}</label>
      <div className="flex gap-2">
        <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="https://..." className="text-sm h-9 flex-1" />
        <label className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-[#0077B6] hover:bg-[#005f92] text-white text-xs font-medium cursor-pointer transition-colors flex-shrink-0">
          <Upload className="w-3 h-3" /> {uploading ? '...' : 'Əlavə et'}
          <input type="file" accept="image/*" className="sr-only" onChange={handleUpload} />
        </label>
      </div>
      {value && <img src={value} alt="" className="mt-2 h-16 rounded-lg object-cover border border-[#E2E8F0]" />}
    </div>
  );
}


function HomepageEditor() {
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState('');
  const [saved, setSaved] = useState('');
  useEffect(() => { api('get', '/api/site-content').then(r => setContent(r.data)).catch(console.error); }, []);
  const saveSection = async (section, data) => {
    setSaving(section);
    try { await api('put', '/api/site-content', { section, content: data }); setSaved(section); setTimeout(() => setSaved(''), 2000); }
    catch { alert('Saxlama xətası'); } finally { setSaving(''); }
  };
  const u = (section, field, value) => setContent(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  const SaveBtn = ({ section, data }) => (
    <Button size="sm" onClick={() => saveSection(section, data)} disabled={saving === section} className="bg-[#0077B6] hover:bg-[#005f92]">
      {saving === section ? '...' : saved === section ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
    </Button>
  );

  const hero = content.hero || {};
  const svc = content.services_section || { items: [{},{},{},{}] };
  const cat = content.categories_section || { items: [{},{},{},{},{},{}] };
  const about = content.about || {};
  const mission = content.mission_section || {};
  const newsletter = content.newsletter_section || {};

  const updateSvcItem = (i, field, val) => { const items = [...(svc.items||[{},{},{},{}])]; while(items.length<4) items.push({}); items[i]={...items[i],[field]:val}; setContent(p=>({...p,services_section:{...svc,items}})); };
  const updateCatItem = (i, field, val) => { const items = [...(cat.items||[{},{},{},{},{},{}])]; while(items.length<6) items.push({}); items[i]={...items[i],[field]:val}; setContent(p=>({...p,categories_section:{...cat,items}})); };

  const svcDef = [{t:'Fərdi Dərslər',d:'Hər şagirdin ehtiyacına uyğun plan',i:'BookOpen'},{t:'Qrup Dərsləri',d:'Kiçik qruplarla interaktiv öyrənmə',i:'Users'},{t:'Dərs Vəsaitləri',d:'PDF, video və interaktiv materiallar',i:'FileText'},{t:'İmtahana Hazırlıq',d:'Qəbul və buraxılış hazırlığı',i:'Target'}];
  const catDef = [
    {t:'Fiziki Coğrafiya',d:'Relyef, iqlim, su ehtiyatları',i:'Mountain',fd:'Fiziki coğrafiya Yer kürəsinin təbii komponentlərini — relyefi, iqlimi, su ehtiyatlarını, torpağı və bitki örtüyünü öyrənir.',tp:'Litosfer və relyef formaları\nAtmosfer və iqlim qurşaqları\nHidrosfer — çaylar, göllər, okeanlar\nTorpaq örtüyü və eroziya\nTəbii fəlakətlər və onların səbəbləri'},
    {t:'İqtisadi Coğrafiya',d:'Əhali, təsərrüfat, dünya ölkələri',i:'Map',fd:'İqtisadi coğrafiya dünya ölkələrinin iqtisadi inkişafını, əhali məskunlaşmasını və beynəlxalq ticarət əlaqələrini öyrənir.',tp:'Əhali coğrafiyası və demoqrafiya\nDünya ölkələrinin iqtisadiyyatı\nTəbii ehtiyatlar və istifadəsi\nSənaye və kənd təsərrüfatı\nBeynəlxalq iqtisadi əlaqələr'},
    {t:'Xəritəşünaslıq',d:'Xəritə oxuma, koordinat sistemi',i:'Layers',fd:'Xəritəşünaslıq xəritələrin yaradılması, oxunması və istifadəsi haqqında elmdir.',tp:'Coğrafi koordinat sistemi\nXəritə proyeksiyaları\nMiqyas və məsafə hesablama\nTopoqrafik xəritələr\nGIS və müasir xəritəçəkmə'},
    {t:'Okeanologiya',d:'Okean və dənizlər, su axınları',i:'Waves',fd:'Okeanologiya dünya okeanlarını, dənizləri, onların fiziki və bioloji xüsusiyyətlərini öyrənir.',tp:'Dünya okeanlarının xüsusiyyətləri\nDəniz axınları və su dövranı\nGel-git hadisələri\nDəniz altı relyefi\nOkeanların ekoloji problemləri'},
    {t:'Meteorologiya',d:'Atmosfer hadisələri, hava proqnozu',i:'Cloud',fd:'Meteorologiya atmosferdəki fiziki prosesləri, hava hadisələrini və iqlim dəyişikliklərini öyrənir.',tp:'Atmosferin quruluşu və tərkibi\nHava kütlələri və frontlar\nSiklon və antisiklonlar\nHava proqnozu metodları\nİqlim dəyişiklikləri'},
    {t:'Biocoğrafiya',d:'Təbii zonalar, flora və fauna',i:'TreePine',fd:'Biocoğrafiya canlı orqanizmlərin Yer kürəsində yayılma qanunauyğunluqlarını öyrənir.',tp:'Təbii zonalar və biomlar\nMeşə ekosistemləri\nSəhra və yarımsəhra zonaları\nQütb və subarktik zonalar\nBiomüxtəlifliyin qorunması'},
  ];
  const allIcons = ['BookOpen','Users','FileText','Target','Globe2','Mountain','Waves','Cloud','Map','Layers','Video','Compass','TreePine'];

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <h1 className="text-2xl font-bold text-[#1A2530] mb-6">Əsas Səhifə Redaktoru</h1>

      {/* HERO */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#1A2530]">Hero Bölməsi</h2><SaveBtn section="hero" data={hero} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge mətni</label><Input value={hero.badge||'dersimizcografiya.az'} onChange={e=>u('hero','badge',e.target.value)} className="h-8" /></div>
          <div></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq sətir 1</label><Input value={hero.title1||'Əsl'} onChange={e=>u('hero','title1',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq sətir 2 (gradient)</label><Input value={hero.title2||'Coğrafiya Təhsili'} onChange={e=>u('hero','title2',e.target.value)} className="h-8" /></div>
          <div className="col-span-2">
            <label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı <span className="text-amber-500">({'{teacher}'} yazsanız müəllim adı qızılı rəngdə görsənəcək)</span></label>
            <Input value={hero.subtitle||''} onChange={e=>u('hero','subtitle',e.target.value)} className="h-8" />
          </div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Müəllim adı (qızılı hərflərlə)</label><Input value={hero.teacher_name||'İSFƏNDİYAR İSGƏNDƏROV'} onChange={e=>u('hero','teacher_name',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={hero.bg_image} onChange={v=>u('hero','bg_image',v)} />
          <div></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 1 mətni</label><Input value={hero.btn1||'Xidmətlər'} onChange={e=>u('hero','btn1',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 1 URL</label><Input value={hero.btn1_url||'/services'} onChange={e=>u('hero','btn1_url',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 2 mətni</label><Input value={hero.btn2||'Yeniliklər'} onChange={e=>u('hero','btn2',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 2 URL</label><Input value={hero.btn2_url||'/news'} onChange={e=>u('hero','btn2_url',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 3 mətni (ixtiyari)</label><Input value={hero.btn3||''} onChange={e=>u('hero','btn3',e.target.value)} className="h-8" placeholder="Əlavə düymə" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 3 URL</label><Input value={hero.btn3_url||''} onChange={e=>u('hero','btn3_url',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 4 mətni (ixtiyari)</label><Input value={hero.btn4||''} onChange={e=>u('hero','btn4',e.target.value)} className="h-8" placeholder="Əlavə düymə" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə 4 URL</label><Input value={hero.btn4_url||''} onChange={e=>u('hero','btn4_url',e.target.value)} className="h-8" /></div>
        </div>
        <h3 className="text-sm font-semibold text-[#1A2530] mt-3 mb-2">Statistika (4 - Hero bölməsi)</h3>
        <div className="grid grid-cols-4 gap-2">
          {[{k:'stat1',v:'15+',l:'İl Təcrübə'},{k:'stat2',v:'500+',l:'Şagird'},{k:'stat3',v:'50+',l:'Kurs'},{k:'stat4',v:'95%',l:'Müvəffəqiyyət'}].map(s=>(
            <div key={s.k} className="space-y-1"><Input value={hero[`${s.k}_value`]||s.v} onChange={e=>u('hero',`${s.k}_value`,e.target.value)} className="text-xs h-7" /><Input value={hero[`${s.k}_label`]||s.l} onChange={e=>u('hero',`${s.k}_label`,e.target.value)} className="text-xs h-7" /></div>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#1A2530]">Xidmətlər ({(svc.items||[]).length} kart)</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { const items=[...(svc.items||[])]; items.push({title:'',desc:'',icon:'BookOpen'}); setContent(p=>({...p,services_section:{...svc,items}})); }}>
              <Plus className="w-3 h-3 mr-1" /> Əlavə et
            </Button>
            <SaveBtn section="services_section" data={svc} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {(svc.items||[]).map((item,i)=>{const d=svcDef[i]||{t:'',d:'',i:'BookOpen'};return(
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5 relative">
              <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#0077B6]">Kart {i+1}</span>
                <select value={item.icon||d.i} onChange={e=>updateSvcItem(i,'icon',e.target.value)} className="h-6 rounded border text-[10px] px-1 bg-white">{allIcons.map(ic=><option key={ic} value={ic}>{ic}</option>)}</select>
                <button onClick={()=>{const items=[...(svc.items||[])];items.splice(i,1);setContent(p=>({...p,services_section:{...svc,items}}));}} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5"/></button></div>
              <Input value={item.title||d.t} onChange={e=>updateSvcItem(i,'title',e.target.value)} className="text-xs h-7" />
              <Input value={item.desc||d.d} onChange={e=>updateSvcItem(i,'desc',e.target.value)} className="text-xs h-7" />
            </div>);})}
        </div>
      </div>

      {/* CATEGORIES */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#1A2530]">Coğrafiya Dünyası ({(cat.items||[]).length} kart)</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { const items=[...(cat.items||[])]; items.push({title:'',desc:'',icon:'Globe2'}); setContent(p=>({...p,categories_section:{...cat,items}})); }}>
              <Plus className="w-3 h-3 mr-1" /> Əlavə et
            </Button>
            <SaveBtn section="categories_section" data={cat} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge mətni</label><Input value={cat.badge||'Dərs Kateqoriyaları'} onChange={e=>setContent(p=>({...p,categories_section:{...cat,badge:e.target.value}}))} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Bölmə başlığı</label><Input value={cat.title||'Coğrafiya Dünyası'} onChange={e=>setContent(p=>({...p,categories_section:{...cat,title:e.target.value}}))} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={cat.subtitle||'Hər mövzuda peşəkar dərs materialları'} onChange={e=>setContent(p=>({...p,categories_section:{...cat,subtitle:e.target.value}}))} className="h-8" /></div>
        </div>
        <div className="mb-3">
          <ImageUploadField label="Bölmə arxa fon şəkli" value={cat.bg_image||''} onChange={v=>setContent(p=>({...p,categories_section:{...cat,bg_image:v}}))} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {(cat.items||[]).map((item,i)=>{const d=catDef[i]||{t:'',d:'',i:'Globe2',fd:'',tp:''};return(
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#0077B6]">{item.title||d.t||`Kart ${i+1}`}</span>
                <select value={item.icon||d.i} onChange={e=>updateCatItem(i,'icon',e.target.value)} className="h-5 rounded border text-[9px] px-1 bg-white">{allIcons.map(ic=><option key={ic} value={ic}>{ic}</option>)}</select>
                <button onClick={()=>{const items=[...(cat.items||[])];items.splice(i,1);setContent(p=>({...p,categories_section:{...cat,items}}));}} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5"/></button></div>
              <Input value={item.title||d.t} onChange={e=>updateCatItem(i,'title',e.target.value)} className="text-xs h-7" />
              <Input value={item.desc||d.d} onChange={e=>updateCatItem(i,'desc',e.target.value)} className="text-xs h-7" />
              <ImageUploadField label="Kart şəkli" value={item.image} onChange={v=>updateCatItem(i,'image',v)} />
              <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Səhifə URL</label><Input value={item.url||`/category/${i}`} onChange={e=>updateCatItem(i,'url',e.target.value)} className="text-xs h-7" /></div>
              <div className="col-span-1"><label className="text-xs text-[#5C6B7A] mb-0.5 block">Ətraflı təsvir (kateqoriya səhifəsi)</label><Textarea value={item.full_desc||d.fd} onChange={e=>updateCatItem(i,'full_desc',e.target.value)} rows={2} className="text-xs" /></div>
              <div className="col-span-1"><label className="text-xs text-[#5C6B7A] mb-0.5 block">Mövzular (hər sətir = 1 mövzu)</label><Textarea value={Array.isArray(item.topics) ? item.topics.join('\n') : (item.topics||d.tp)} onChange={e=>updateCatItem(i,'topics',e.target.value.split('\n').filter(t=>t.trim()))} rows={3} className="text-xs" /></div>
              <div className="col-span-1">
                <label className="text-xs text-[#5C6B7A] mb-1 block">Vizual Materiallar (3 şəkil)</label>
                <div className="space-y-1.5">
                  {[0,1,2].map(gi => {
                    const gal = item.gallery || [];
                    return <ImageUploadField key={gi} label={`Şəkil ${gi+1}`} value={gal[gi]||''} onChange={v=>{const g=[...(item.gallery||['','',''])];while(g.length<3)g.push('');g[gi]=v;updateCatItem(i,'gallery',g);}} />;
                  })}
                </div>
              </div>
            </div>);})}
        </div>
      </div>

      {/* ABOUT */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#1A2530]">Haqqımızda Bölməsi</h2><SaveBtn section="about" data={about} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge mətni</label><Input value={about.badge||'Haqqımızda'} onChange={e=>u('about','badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={about.title||'Biz Kimik?'} onChange={e=>u('about','title',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Təsvir</label><Textarea value={about.description||'Peşəkar coğrafiya müəllimi olaraq şagird və tələbələrə keyfiyyətli təhsil xidməti göstəririk. Uzun illər təcrübəmizlə coğrafiyanı maraqlı və əyləncəli şəkildə öyrədirik.'} onChange={e=>u('about','description',e.target.value)} rows={2} /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Fəaliyyət ili</label><Input value={about.year||'2011'} onChange={e=>u('about','year',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Fəaliyyət ili etiketi</label><Input value={about.year_label||'fəaliyyət ili'} onChange={e=>u('about','year_label',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Keyfiyyət %</label><Input value={about.quality||'99%'} onChange={e=>u('about','quality',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Keyfiyyət etiketi</label><Input value={about.quality_label||'keyfiyyət'} onChange={e=>u('about','quality_label',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Uğur nisbəti</label><Input value={about.success||'95%'} onChange={e=>u('about','success',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Uğur etiketi</label><Input value={about.success_label||'uğur nisbəti'} onChange={e=>u('about','success_label',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Əsas şəkil" value={about.image} onChange={v=>u('about','image',v)} />
          <ImageUploadField label="Kiçik şəkil (sol üst)" value={about.volcano_image} onChange={v=>u('about','volcano_image',v)} />
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə mətni</label><Input value={about.btn_text||'Ətraflı Oxu'} onChange={e=>u('about','btn_text',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə URL</label><Input value={about.btn_url||'/about'} onChange={e=>u('about','btn_url',e.target.value)} className="h-8" /></div>
        </div>
      </div>

      {/* MISSION/VISION */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#1A2530]">Missiya & Vizyon</h2><SaveBtn section="mission_section" data={mission} /></div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Bölmə başlığı</label><Input value={mission.section_title||'Dağlar Qədər Möhkəm Dəyərlər'} onChange={e=>u('mission_section','section_title',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={mission.bg_image} onChange={v=>u('mission_section','bg_image',v)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
            <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Badge</label><Input value={mission.mission_badge||'Missiyamız'} onChange={e=>u('mission_section','mission_badge',e.target.value)} className="text-xs h-7" /></div>
            <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Başlıq</label><Input value={mission.mission_title||'Bizim Missiyamız'} onChange={e=>u('mission_section','mission_title',e.target.value)} className="text-xs h-7" /></div>
            <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Mətn</label><Textarea value={mission.mission_text||'Şagird və tələbələrə ən keyfiyyətli coğrafiya təhsili verərək, onların dünyamızı daha yaxşı anlamalarına töhfə vermək.'} onChange={e=>u('mission_section','mission_text',e.target.value)} rows={2} className="text-xs" /></div>
          </div>
          <div className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
            <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Badge</label><Input value={mission.vision_badge||'Vizyonumuz'} onChange={e=>u('mission_section','vision_badge',e.target.value)} className="text-xs h-7" /></div>
            <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Başlıq</label><Input value={mission.vision_title||'Bizim Vizyonumuz'} onChange={e=>u('mission_section','vision_title',e.target.value)} className="text-xs h-7" /></div>
            <div><label className="text-xs text-[#5C6B7A] mb-0.5 block">Mətn</label><Textarea value={mission.vision_text||'Azərbaycanda coğrafiya təhsilinin inkişafına töhfə verən, müasir texnologiyalarla dəstəklənən ən etibarlı platform olmaq.'} onChange={e=>u('mission_section','vision_text',e.target.value)} rows={2} className="text-xs" /></div>
          </div>
        </div>
      </div>

      {/* NEWSLETTER */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#1A2530]">Abunə Ol! Bölməsi</h2><SaveBtn section="newsletter_section" data={newsletter} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={newsletter.title||'Abunə ol!'} onChange={e=>u('newsletter_section','title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={newsletter.subtitle||'Yeni dərs materialları və yeniliklərdən xəbərdar olun'} onChange={e=>u('newsletter_section','subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={newsletter.bg_image} onChange={v=>u('newsletter_section','bg_image',v)} />
        </div>
      </div>
    </div>
  );
}


function AboutPageEditor() {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data.about_page || {})).catch(console.error); }, []);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'about_page', content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };
  const updateHL = (i, f, v) => { const h = [...(pg.highlights || [{},{},{},{}])]; while(h.length<4) h.push({}); h[i]={...h[i],[f]:v}; u('highlights',h); };
  const updateVal = (i, f, v) => { const vals = [...(pg.values || [{},{},{},{},{}])]; while(vals.length<5) vals.push({}); vals[i]={...vals[i],[f]:v}; u('values',vals); };

  const hlDef = [{t:'Peşəkar Təhsil',d:'BDU Coğrafiya fakültəsi məzunu'},{t:'15+ İl Təcrübə',d:'Uzun illər pedaqoji fəaliyyət'},{t:'50+ Kurs',d:'Müxtəlif mövzularda dərs proqramları'},{t:'500+ Şagird',d:'Uğurla hazırlanmış tələbə və şagirdlər'}];
  const valDef = ['Keyfiyyət və etibarlılıq','Şagird məmnuniyyəti','Peşəkarlıq','İnnovasiya','Şəffaflıq'];

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">Haqqımızda Səhifəsi Redaktoru</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
          {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Hamısını Saxla</>}
        </Button>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Hero Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.hero_badge||'Haqqımızda'} onChange={e=>u('hero_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.hero_title||'Biz Kimik?'} onChange={e=>u('hero_title',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.hero_subtitle||'Peşəkar coğrafiya müəllimi olaraq şagird və tələbələrə keyfiyyətli təhsil xidməti göstəririk'} onChange={e=>u('hero_subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={pg.hero_bg} onChange={v=>u('hero_bg',v)} />
        </div>
      </div>

      {/* Teacher */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Müəllim Profili</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.teacher_badge||'Müəllim'} onChange={e=>u('teacher_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Ad/Başlıq</label><Input value={pg.teacher_name||'Coğrafiya Müəllimi'} onChange={e=>u('teacher_name',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Təsvir 1</label><Textarea value={pg.teacher_desc1||''} onChange={e=>u('teacher_desc1',e.target.value)} rows={2} /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Təsvir 2</label><Textarea value={pg.teacher_desc2||''} onChange={e=>u('teacher_desc2',e.target.value)} rows={2} /></div>
          <ImageUploadField label="Müəllim şəkli" value={pg.teacher_image} onChange={v=>u('teacher_image',v)} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-[#5C6B7A] mb-1 block">Təcrübə rəqəmi</label><Input value={pg.teacher_exp||'15+'} onChange={e=>u('teacher_exp',e.target.value)} className="h-8" /></div>
            <div><label className="text-xs text-[#5C6B7A] mb-1 block">Təcrübə etiketi</label><Input value={pg.teacher_exp_label||'İl Təcrübə'} onChange={e=>u('teacher_exp_label',e.target.value)} className="h-8" /></div>
          </div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Təhsil</label><Input value={pg.teacher_edu||'BDU - Coğrafiya'} onChange={e=>u('teacher_edu',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">İxtisas</label><Input value={pg.teacher_spec||'Coğrafiya Müəllimi'} onChange={e=>u('teacher_spec',e.target.value)} className="h-8" /></div>
        </div>
      </div>

      {/* Highlights (4 kart) */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3 flex items-center justify-between">Statistika Kartları ({(pg.highlights||[]).length})
          <Button size="sm" variant="outline" onClick={() => { const h=[...(pg.highlights||[])]; h.push({title:'',desc:'',icon:'Award'}); u('highlights',h); }}>
            <Plus className="w-3 h-3 mr-1" /> Əlavə et
          </Button>
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {(pg.highlights||[]).map((item,i) => { const d=hlDef[i]||{t:'',d:''}; return (
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#0077B6]">Kart {i+1}</span>
                <select value={item.icon||['GraduationCap','Award','BookOpen','Users'][i]||'Award'} onChange={e=>updateHL(i,'icon',e.target.value)} className="h-6 rounded border text-[10px] px-1 bg-white">
                  {['GraduationCap','Award','BookOpen','Users','Shield','Heart','Lightbulb','Eye','Globe2','Mountain'].map(ic=><option key={ic} value={ic}>{ic}</option>)}
                </select>
                <button onClick={()=>{const h=[...(pg.highlights||[])];h.splice(i,1);u('highlights',h);}} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5"/></button>
              </div>
              <Input value={item.title||d.t} onChange={e=>updateHL(i,'title',e.target.value)} placeholder="Başlıq" className="text-xs h-7" />
              <Input value={item.desc||d.d} onChange={e=>updateHL(i,'desc',e.target.value)} placeholder="Təsvir" className="text-xs h-7" />
            </div>
          );})}
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Missiya & Vizyon</h2>
        <ImageUploadField label="Bölmə şəkli" value={pg.mission_image} onChange={v=>u('mission_image',v)} />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
            <div><label className="text-xs text-[#0077B6] font-bold">Missiya</label></div>
            <Input value={pg.mission_badge||'Missiya'} onChange={e=>u('mission_badge',e.target.value)} className="text-xs h-7" placeholder="Badge" />
            <Input value={pg.mission_title||'Bizim Missiyamız'} onChange={e=>u('mission_title',e.target.value)} className="text-xs h-7" placeholder="Başlıq" />
            <Textarea value={pg.mission_text||''} onChange={e=>u('mission_text',e.target.value)} rows={2} className="text-xs" placeholder="Mətn" />
          </div>
          <div className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
            <div><label className="text-xs text-[#00B4D8] font-bold">Vizyon</label></div>
            <Input value={pg.vision_badge||'Vizyon'} onChange={e=>u('vision_badge',e.target.value)} className="text-xs h-7" placeholder="Badge" />
            <Input value={pg.vision_title||'Bizim Vizyonumuz'} onChange={e=>u('vision_title',e.target.value)} className="text-xs h-7" placeholder="Başlıq" />
            <Textarea value={pg.vision_text||''} onChange={e=>u('vision_text',e.target.value)} rows={2} className="text-xs" placeholder="Mətn" />
          </div>
        </div>
      </div>

      {/* Values (5 kart) */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3 flex items-center justify-between">Dəyərlərimiz ({(pg.values||[]).length} kart)
          <Button size="sm" variant="outline" onClick={() => { const v=[...(pg.values||[])]; v.push({title:'',icon:'Shield'}); u('values',v); }}>
            <Plus className="w-3 h-3 mr-1" /> Əlavə et
          </Button>
        </h2>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.values_badge||'Dəyərlərimiz'} onChange={e=>u('values_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.values_title||'Bizim Dəyərlərimiz'} onChange={e=>u('values_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.values_subtitle||'Bizim işimizdə əsas qalan dəyərlər'} onChange={e=>u('values_subtitle',e.target.value)} className="h-8" /></div>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {(pg.values||[]).map((val,i) => { return (
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-2 space-y-1">
              <div className="flex items-center gap-1">
                <select value={val.icon||['Shield','Heart','Award','Lightbulb','Eye'][i]||'Shield'} onChange={e=>updateVal(i,'icon',e.target.value)} className="w-full h-6 rounded border text-[10px] px-1 bg-white flex-1">
                  {['Shield','Heart','Award','Lightbulb','Eye','GraduationCap','BookOpen','Users','Globe2','Mountain'].map(ic=><option key={ic} value={ic}>{ic}</option>)}
                </select>
                <button onClick={()=>{const v=[...(pg.values||[])];v.splice(i,1);u('values',v);}} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-3 h-3"/></button>
              </div>
              <Input value={val.title||valDef[i]} onChange={e=>updateVal(i,'title',e.target.value)} className="text-xs h-7" />
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}


function ServicesPageEditor() {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data.services_page || {})).catch(console.error); }, []);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'services_page', content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };
  const updateSvc = (i, f, v) => { const s = [...(pg.services || [{},{},{},{}])]; while(s.length<4) s.push({}); s[i]={...s[i],[f]:v}; u('services',s); };
  const updateSub = (i, f, v) => { const s = [...(pg.subjects || [{},{},{},{},{},{}])]; while(s.length<6) s.push({}); s[i]={...s[i],[f]:v}; u('subjects',s); };

  const svcDef = [{t:'Fərdi Dərslər',d:'Hər şagirdin səviyyəsinə uyğun fərdi dərs planı.',i:'BookOpen'},{t:'Qrup Dərsləri',d:'3-5 nəfərlik kiçik qruplarda interaktiv dərslər.',i:'Users'},{t:'Dərs Vəsaitləri',d:'PDF dərs qeydləri, sxemlər, xəritələr.',i:'FileText'},{t:'Video Dərslər',d:'Mövzuların ətraflı video izahları.',i:'Video'}];
  const subDef = [{t:'Fiziki Coğrafiya',d:'Yer kürəsinin quruluşu, iqlim, relyef.',i:'Globe2'},{t:'İqtisadi Coğrafiya',d:'Dünya ölkələri, əhali, təsərrüfat.',i:'Map'},{t:'Xəritəşünaslıq',d:'Xəritə oxuma, koordinat sistemi.',i:'Layers'},{t:'Geomorfologiya',d:'Dağlar, düzənliklər, vulkanlar.',i:'Mountain'},{t:'Okeanologiya',d:'Okean və dənizlər, su axınları.',i:'Waves'},{t:'İmtahana Hazırlıq',d:'Buraxılış və qəbul imtahanları.',i:'Target'}];
  const allIcons = ['BookOpen','Users','FileText','Target','Video','Globe2','Map','Layers','Mountain','Waves','Cloud','Compass','TreePine'];

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">Xidmətlər Səhifəsi Redaktoru</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
          {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Hamısını Saxla</>}
        </Button>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Hero Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.hero_badge||'Xidmətlərimiz'} onChange={e=>u('hero_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.hero_title||'Xidmətlərimiz'} onChange={e=>u('hero_title',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.hero_subtitle||'Vulkanlardan buzlaqlara, okeandan atmosferə — coğrafiyanın hər sahəsində peşəkar xidmət'} onChange={e=>u('hero_subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={pg.hero_bg} onChange={v=>u('hero_bg',v)} />
        </div>
      </div>

      {/* Our Services (new) */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3 flex items-center justify-between">Bizim Xidmətlərimiz ({(pg.our_services||[]).length} kart)
          <Button size="sm" variant="outline" onClick={() => { const s=[...(pg.our_services||[])]; s.push({title:'',desc:'',icon:'BookOpen',url:'/contact'}); u('our_services',s); }}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
        </h2>
        <div className="mb-3"><label className="text-xs text-[#5C6B7A] mb-1 block">Bölmə başlığı</label><Input value={pg.our_svc_title||'Bizim Xidmətlərimiz'} onChange={e=>u('our_svc_title',e.target.value)} className="h-8" /></div>
        <div className="grid grid-cols-3 gap-3">
          {(pg.our_services||[]).map((item,i)=>(
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#0077B6]">Kart {i+1}</span>
                <select value={item.icon||'BookOpen'} onChange={e=>{const s=[...(pg.our_services||[])];s[i]={...s[i],icon:e.target.value};u('our_services',s);}} className="h-6 rounded border text-[10px] px-1 bg-white">
                  {['BookOpen','GraduationCap','Award','Target','Users','FileText','Globe2','Mountain','Layers','Video'].map(ic=><option key={ic} value={ic}>{ic}</option>)}
                </select>
                <button onClick={()=>{const s=[...(pg.our_services||[])];s.splice(i,1);u('our_services',s);}} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5"/></button>
              </div>
              <Input value={item.title||''} onChange={e=>{const s=[...(pg.our_services||[])];s[i]={...s[i],title:e.target.value};u('our_services',s);}} placeholder="Başlıq" className="text-xs h-7" />
              <Input value={item.desc||''} onChange={e=>{const s=[...(pg.our_services||[])];s[i]={...s[i],desc:e.target.value};u('our_services',s);}} placeholder="Təsvir" className="text-xs h-7" />
              <Input value={item.url||`/service/${i}`} onChange={e=>{const s=[...(pg.our_services||[])];s[i]={...s[i],url:e.target.value};u('our_services',s);}} placeholder="URL" className="text-xs h-7" />
              <Textarea value={item.full_desc||''} onChange={e=>{const s=[...(pg.our_services||[])];s[i]={...s[i],full_desc:e.target.value};u('our_services',s);}} placeholder="Ətraflı təsvir (səhifə məzmunu)" rows={2} className="text-xs" />
              <Textarea value={Array.isArray(item.topics)?item.topics.join('\n'):(item.topics||'')} onChange={e=>{const s=[...(pg.our_services||[])];s[i]={...s[i],topics:e.target.value.split('\n').filter(t=>t.trim())};u('our_services',s);}} placeholder="Mövzular (hər sətir = 1 mövzu)" rows={3} className="text-xs" />
            </div>
          ))}
        </div>
      </div>

      {/* Services (4 kart) */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3 flex items-center justify-between">Təhsil Xidmətləri ({(pg.services||[]).length} kart)
          <Button size="sm" variant="outline" onClick={() => { const s=[...(pg.services||[])]; s.push({title:'',desc:'',icon:'BookOpen'}); u('services',s); }}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
        </h2>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.svc_badge||'Təhsil Xidmətləri'} onChange={e=>u('svc_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.svc_title||'Necə Öyrədirik?'} onChange={e=>u('svc_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.svc_subtitle||'Hər şagirdin ehtiyacına uyğun peşəkar yanaşma'} onChange={e=>u('svc_subtitle',e.target.value)} className="h-8" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {(pg.services||[]).map((item,i)=>{const d=svcDef[i]||{t:'',d:'',i:'BookOpen'};return(
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#0077B6]">Kart {i+1}</span>
                <select value={item.icon||d.i} onChange={e=>updateSvc(i,'icon',e.target.value)} className="h-6 rounded border text-[10px] px-1 bg-white">{allIcons.map(ic=><option key={ic} value={ic}>{ic}</option>)}</select>
                <button onClick={()=>{const s=[...(pg.services||[])];s.splice(i,1);u('services',s);}} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5"/></button></div>
              <Input value={item.title||d.t} onChange={e=>updateSvc(i,'title',e.target.value)} className="text-xs h-7" />
              <Input value={item.desc||d.d} onChange={e=>updateSvc(i,'desc',e.target.value)} className="text-xs h-7" />
              <ImageUploadField label="Kart şəkli" value={item.image} onChange={v=>updateSvc(i,'image',v)} />
            </div>);})}
        </div>
      </div>

      {/* Subjects (6 kart) */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3 flex items-center justify-between">Coğrafiya Sahələri ({(pg.subjects||[]).length} kart)
          <Button size="sm" variant="outline" onClick={() => { const s=[...(pg.subjects||[])]; s.push({title:'',desc:'',icon:'Globe2'}); u('subjects',s); }}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
        </h2>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.sub_badge||'Fənn Sahələri'} onChange={e=>u('sub_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.sub_title||'Coğrafiya Sahələri'} onChange={e=>u('sub_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.sub_subtitle||'Hər sahədə dərin bilik və praktiki təcrübə'} onChange={e=>u('sub_subtitle',e.target.value)} className="h-8" /></div>
        </div>
        <ImageUploadField label="Bölmə arxa fon şəkli" value={pg.sub_bg} onChange={v=>u('sub_bg',v)} />
        <div className="grid grid-cols-3 gap-3 mt-3">
          {(pg.subjects||[]).map((item,i)=>{const d=subDef[i]||{t:'',d:'',i:'Globe2'};return(
            <div key={i} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2"><span className="text-xs font-bold text-[#0077B6]">{item.title||d.t||`Kart ${i+1}`}</span>
                <select value={item.icon||d.i} onChange={e=>updateSub(i,'icon',e.target.value)} className="h-5 rounded border text-[9px] px-1 bg-white">{allIcons.map(ic=><option key={ic} value={ic}>{ic}</option>)}</select>
                <button onClick={()=>{const s=[...(pg.subjects||[])];s.splice(i,1);u('subjects',s);}} className="ml-auto text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5"/></button></div>
              <Input value={item.title||d.t} onChange={e=>updateSub(i,'title',e.target.value)} className="text-xs h-7" />
              <Input value={item.desc||d.d} onChange={e=>updateSub(i,'desc',e.target.value)} className="text-xs h-7" />
              <ImageUploadField label="Kart şəkli" value={item.image} onChange={v=>updateSub(i,'image',v)} />
            </div>);})}
        </div>
      </div>

      {/* Parallax */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Parallax Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.parallax_title||'Atmosferdən Kainata'} onChange={e=>u('parallax_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.parallax_subtitle||'Yer kürəsinin hər qatını birlikdə kəşf edəcəyik'} onChange={e=>u('parallax_subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={pg.parallax_bg} onChange={v=>u('parallax_bg',v)} />
        </div>
      </div>

      {/* CTA */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">CTA (Əlaqə) Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.cta_title||'Sualınız Var?'} onChange={e=>u('cta_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.cta_subtitle||'Bizimlə əlaqə saxlayın'} onChange={e=>u('cta_subtitle',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə mətni</label><Input value={pg.cta_btn_text||'Bizimlə Əlaqə'} onChange={e=>u('cta_btn_text',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə URL</label><Input value={pg.cta_btn_url||'/contact'} onChange={e=>u('cta_btn_url',e.target.value)} className="h-8" /></div>
        </div>
      </div>
    </div>
  );
}



function NewsPageFullEditor({ newsItems, setShowDialog, setEditItem, setDeleteConfirm, fetchAll }) {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data.news_page || {})).catch(console.error); }, []);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'news_page', content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <h1 className="text-2xl font-bold text-[#1A2530] mb-6">Yeniliklər Səhifəsi Redaktoru</h1>

      {/* Hero Settings */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#1A2530]">Hero Bölməsi</h2>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
            {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.hero_badge||'Son Yeniliklər'} onChange={e=>u('hero_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.hero_title||'Yeniliklər'} onChange={e=>u('hero_title',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.hero_subtitle||''} onChange={e=>u('hero_subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={pg.hero_bg} onChange={v=>u('hero_bg',v)} />
        </div>
      </div>

      {/* News List */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#1A2530]">Xəbər Məzmunları ({newsItems.length})</h2>
          <Button data-testid="add-news-btn" onClick={() => { setEditItem(null); setShowDialog('news'); }} className="bg-[#0077B6] hover:bg-[#005f92]">
            <Plus className="w-4 h-4 mr-2" /> Yeni Xəbər
          </Button>
        </div>
        <div className="space-y-3">
          {newsItems.map(n => (
            <div key={n.id} className="bg-[#F4F7F6] rounded-xl p-4 flex items-center gap-4" data-testid={`admin-news-${n.id}`}>
              {n.image_url && <img src={n.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1A2530] truncate">{n.title}</div>
                <div className="text-xs text-[#5C6B7A] mt-1">
                  {n.category} | {(() => { const d=new Date(n.created_at); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })()} | {n.is_published ? 'Yayımlanmış' : 'Qaralama'}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="icon" variant="ghost" onClick={() => { setEditItem(n); setShowDialog('news'); }}><Edit className="w-4 h-4 text-[#5C6B7A]" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setDeleteConfirm({type: 'news', id: n.id, name: n.title})}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
          {newsItems.length === 0 && <p className="text-center py-8 text-sm text-[#5C6B7A]">Hələ xəbər yoxdur</p>}
        </div>
      </div>
    </div>
  );
}



function ContactPageEditor() {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data.contact_page || {})).catch(console.error); }, []);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'contact_page', content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };

  const extras = pg.extra_contacts || [];
  const addExtra = () => setPg(p => ({...p, extra_contacts: [...(p.extra_contacts||[]), {type:'email',label:'',value:''}]}));
  const updateExtra = (i, f, v) => { const e=[...(pg.extra_contacts||[])]; e[i]={...e[i],[f]:v}; setPg(p=>({...p,extra_contacts:e})); };
  const removeExtra = (i) => { const e=[...(pg.extra_contacts||[])]; e.splice(i,1); setPg(p=>({...p,extra_contacts:e})); };

  const ff = pg.form_fields || {};
  const updateFF = (f, v) => setPg(p => ({...p, form_fields: {...(p.form_fields||{}), [f]: v}}));

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">Əlaqə Səhifəsi Redaktoru</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
          {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Hamısını Saxla</>}
        </Button>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Hero Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.hero_badge||'Əlaqə'} onChange={e=>u('hero_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.hero_title||'Bizimlə Əlaqə'} onChange={e=>u('hero_title',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.hero_subtitle||''} onChange={e=>u('hero_subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={pg.hero_bg} onChange={v=>u('hero_bg',v)} />
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Əlaqə Məlumatları</h2>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Bölmə başlığı</label><Input value={pg.info_title||'Əlaqə Məlumatları'} onChange={e=>u('info_title',e.target.value)} className="h-8" /></div>
          <div></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">E-poçt</label><Input value={pg.email||'info@dersimizcografiya.az'} onChange={e=>u('email',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Telefon</label><Input value={pg.phone||'+994 50 XXX XX XX'} onChange={e=>u('phone',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Ünvan</label><Input value={pg.address||'Bakı, Azərbaycan'} onChange={e=>u('address',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Ünvan xəritə linki (ünvana klikləyəndə açılacaq)</label><Input value={pg.address_map_url||''} onChange={e=>u('address_map_url',e.target.value)} className="h-8" placeholder="https://www.google.com/maps/..." /></div>
        </div>
        {/* Extra Contacts */}
        <div className="border-t border-[#E2E8F0] pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1A2530]">Əlavə Əlaqə Məlumatları ({extras.length})</span>
            <Button size="sm" variant="outline" onClick={addExtra}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
          </div>
          {extras.map((ec, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <select value={ec.type} onChange={e=>updateExtra(i,'type',e.target.value)} className="h-8 rounded-md border border-input px-2 text-sm w-28">
                <option value="email">E-poçt</option><option value="phone">Telefon</option><option value="address">Ünvan</option>
              </select>
              <Input value={ec.label} onChange={e=>updateExtra(i,'label',e.target.value)} placeholder="Etiket" className="h-8 w-28" />
              <Input value={ec.value} onChange={e=>updateExtra(i,'value',e.target.value)} placeholder="Dəyər" className="h-8 flex-1" />
              <button onClick={()=>removeExtra(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Form Settings */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Mesaj Formu Parametrləri</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Form başlığı</label><Input value={pg.form_title||'Mesaj Göndər'} onChange={e=>u('form_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə mətni</label><Input value={ff.button_text||'Göndər'} onChange={e=>updateFF('button_text',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Ad sahəsi etiketi</label><Input value={ff.name_label||'Adınız'} onChange={e=>updateFF('name_label',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">E-poçt sahəsi etiketi</label><Input value={ff.email_label||'E-poçt'} onChange={e=>updateFF('email_label',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Mövzu sahəsi etiketi</label><Input value={ff.subject_label||'Mövzu'} onChange={e=>updateFF('subject_label',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Mesaj sahəsi etiketi</label><Input value={ff.message_label||'Mesaj'} onChange={e=>updateFF('message_label',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Uğur başlığı</label><Input value={ff.success_title||'Göndərildi!'} onChange={e=>updateFF('success_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Uğur mətni</label><Input value={ff.success_text||'Mesajınız uğurla göndərildi.'} onChange={e=>updateFF('success_text',e.target.value)} className="h-8" /></div>
        </div>
        <div className="mt-3 p-3 bg-[#0077B6]/5 rounded-lg">
          <p className="text-xs text-[#0077B6]">Mesajlar həm Admin Panel → İdarəetmə → Mesajlar tab-ında saxlanılır, həm də <strong>Notification@dersimizcografiya.az</strong> e-poçtuna göndərilir (SMTP: Microsoft 365).</p>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Xəritə</h2>
        <div><label className="text-xs text-[#5C6B7A] mb-1 block">Google Maps Embed URL</label>
          <Input value={pg.map_embed||''} onChange={e=>u('map_embed',e.target.value)} className="h-8" placeholder="https://www.google.com/maps/embed?pb=..." />
          <p className="text-[10px] text-[#5C6B7A] mt-1">Google Maps → Paylaş → Embed a map → HTML-dən src="..." URL-ni kopyalayın</p>
        </div>
        {pg.map_embed && (
          <div className="mt-3 rounded-xl overflow-hidden border border-[#E2E8F0]"><iframe src={pg.map_embed} width="100%" height="200" style={{border:0}} title="Preview" /></div>
        )}
      </div>
    </div>
  );
}


function HeaderEditor() {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data.header_settings || {})).catch(console.error); }, []);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'header_settings', content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };
  const socials = pg.socials || [];

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">Header Redaktoru</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
          {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Hamısını Saxla</>}
        </Button>
      </div>

      {/* Top Bar */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Üst Bar (Top Bar)</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Telefon</label><Input value={pg.phone||'+994 50 XXX XX XX'} onChange={e=>u('phone',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">E-poçt</label><Input value={pg.email||'info@dersimizcografiya.az'} onChange={e=>u('email',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Slogan</label><Input value={pg.slogan||'Təhsil sahəsində etibarlı tərəfdaş'} onChange={e=>u('slogan',e.target.value)} className="h-8" /></div>
        </div>
        <div className="mt-3 border-t border-[#E2E8F0] pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1A2530]">Sosial Şəbəkələr ({socials.length})</span>
            <Button size="sm" variant="outline" onClick={() => u('socials',[...socials, {name:'Facebook',url:''}])}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
          </div>
          {socials.map((s,i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <select value={s.name} onChange={e => { const ss=[...socials]; ss[i]={...ss[i],name:e.target.value}; u('socials',ss); }} className="h-8 rounded-md border border-input px-2 text-sm w-28">
                <option>Facebook</option><option>Instagram</option><option>LinkedIn</option><option>WhatsApp</option><option>YouTube</option>
              </select>
              <Input value={s.url} onChange={e => { const ss=[...socials]; ss[i]={...ss[i],url:e.target.value}; u('socials',ss); }} placeholder="https://..." className="h-8 flex-1" />
              <button onClick={() => { const ss=[...socials]; ss.splice(i,1); u('socials',ss); }} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Navbar */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Naviqasiya Barı</h2>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Sayt adı</label><Input value={pg.site_name||'Dərsimiz Coğrafiya'} onChange={e=>u('site_name',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Logo (şəkil yüklə)" value={pg.site_logo} onChange={v=>u('site_logo',v)} />
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">CTA düymə mətni</label><Input value={pg.cta_text||'Bizimlə Əlaqə'} onChange={e=>u('cta_text',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">CTA düymə URL</label><Input value={pg.cta_url||'/contact'} onChange={e=>u('cta_url',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">"Daxil ol" düymə mətni</label><Input value={pg.login_text||'Daxil ol'} onChange={e=>u('login_text',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">"Daxil ol" düymə URL</label><Input value={pg.login_url||'/student'} onChange={e=>u('login_url',e.target.value)} className="h-8" /></div>
          <div className="col-span-2">
            <label className="text-xs text-[#5C6B7A] mb-1 block">Düymə sırası</label>
            <select value={pg.button_order||'cta_first'} onChange={e=>u('button_order',e.target.value)} className="h-8 rounded-md border border-input px-3 text-sm w-full">
              <option value="cta_first">Əlaqə | Daxil ol</option>
              <option value="login_first">Daxil ol | Əlaqə</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

function FooterEditor() {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data.footer_settings || {})).catch(console.error); }, []);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'footer_settings', content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };
  const socials = pg.socials || [];
  const quickLinks = pg.quick_links || [];

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">Footer Redaktoru</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
          {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Hamısını Saxla</>}
        </Button>
      </div>

      {/* Brand */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Brend Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Sayt adı</label><Input value={pg.site_name||'Dərsimiz Coğrafiya'} onChange={e=>u('site_name',e.target.value)} className="h-8" /></div>
          <div></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Təsvir</label><Textarea value={pg.description||'Coğrafiya təhsili sahəsində peşəkar xidmət göstərən etibarlı təhsil platforması.'} onChange={e=>u('description',e.target.value)} rows={2} /></div>
        </div>
        <div className="mt-3 border-t border-[#E2E8F0] pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#1A2530]">Sosial Şəbəkələr ({socials.length})</span>
            <Button size="sm" variant="outline" onClick={() => u('socials',[...socials, {name:'Facebook',url:''}])}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
          </div>
          {socials.map((s,i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <select value={s.name} onChange={e => { const ss=[...socials]; ss[i]={...ss[i],name:e.target.value}; u('socials',ss); }} className="h-8 rounded-md border border-input px-2 text-sm w-28">
                <option>Facebook</option><option>Instagram</option><option>LinkedIn</option><option>WhatsApp</option><option>YouTube</option>
              </select>
              <Input value={s.url} onChange={e => { const ss=[...socials]; ss[i]={...ss[i],url:e.target.value}; u('socials',ss); }} placeholder="https://..." className="h-8 flex-1" />
              <button onClick={() => { const ss=[...socials]; ss.splice(i,1); u('socials',ss); }} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#1A2530]">Sürətli Keçidlər ({quickLinks.length})</h2>
          <Button size="sm" variant="outline" onClick={() => u('quick_links',[...quickLinks, {to:'/',label:''}])}><Plus className="w-3 h-3 mr-1" /> Əlavə et</Button>
        </div>
        {quickLinks.map((l,i) => (
          <div key={i} className="flex gap-2 mb-2 items-center">
            <Input value={l.label} onChange={e => { const ll=[...quickLinks]; ll[i]={...ll[i],label:e.target.value}; u('quick_links',ll); }} placeholder="Başlıq" className="h-8 w-40" />
            <Input value={l.to} onChange={e => { const ll=[...quickLinks]; ll[i]={...ll[i],to:e.target.value}; u('quick_links',ll); }} placeholder="/url" className="h-8 flex-1" />
            <button onClick={() => { const ll=[...quickLinks]; ll.splice(i,1); u('quick_links',ll); }} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      {/* Contact & Hours */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Əlaqə & İş Saatları</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">E-poçt</label><Input value={pg.email||'info@dersimizcografiya.az'} onChange={e=>u('email',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Telefon</label><Input value={pg.phone||'+994 50 XXX XX XX'} onChange={e=>u('phone',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Ünvan</label><Input value={pg.address||'Bakı şəhəri, Azərbaycan'} onChange={e=>u('address',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Xəritə linki (ünvana klikləyəndə açılacaq)</label><Input value={pg.map_url||''} onChange={e=>u('map_url',e.target.value)} className="h-8" placeholder="https://www.google.com/maps/..." /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">İş günləri</label><Input value={pg.work_days||'Bazar ertəsi - Cümə'} onChange={e=>u('work_days',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">İş saatları</label><Input value={pg.work_hours||'09:00 - 18:00'} onChange={e=>u('work_hours',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">İzləyin başlığı</label><Input value={pg.follow_title||'Bizi İzləyin'} onChange={e=>u('follow_title',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">İzləyin mətni</label><Input value={pg.follow_text||'Yeni dərslər və kampaniyalardan xəbərdar olun.'} onChange={e=>u('follow_text',e.target.value)} className="h-8" /></div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Alt Bar</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Copyright mətni</label><Input value={pg.copyright||`Dərsimiz Coğrafiya © 2010-${new Date().getFullYear()}. Bütün hüquqlar qorunur.`} onChange={e=>u('copyright',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Məxfilik linki mətni</label><Input value={pg.privacy_text||'Məxfilik Siyasəti'} onChange={e=>u('privacy_text',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">İstifadə şərtləri mətni</label><Input value={pg.terms_text||'İstifadə Şərtləri'} onChange={e=>u('terms_text',e.target.value)} className="h-8" /></div>
        </div>
      </div>
    </div>
  );
}

function LegalPagesEditor() {
  const [privacy, setPrivacy] = useState('');
  const [terms, setTerms] = useState('');
  const [saving, setSaving] = useState('');
  const [saved, setSaved] = useState('');

  useEffect(() => {
    api('get', '/api/site-content').then(r => {
      setPrivacy(r.data?.privacy_policy?.content || '');
      setTerms(r.data?.terms_of_service?.content || '');
    }).catch(console.error);
  }, []);

  const saveSection = async (section, content) => {
    setSaving(section);
    try {
      const now = new Date().toLocaleDateString('az-AZ');
      await api('put', '/api/site-content', { section, content: { content, updated_at: now } });
      setSaved(section); setTimeout(() => setSaved(''), 2000);
    } catch { alert('Xəta'); }
    finally { setSaving(''); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <h1 className="text-2xl font-bold text-[#1A2530] mb-6">Hüquqi Səhifələr</h1>
      <p className="text-sm text-[#5C6B7A] mb-6">Markdown formatında yazın. Başlıqlar üçün #, siyahı üçün -, qalın mətn üçün **mətn** istifadə edin.</p>

      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#1A2530]">Məxfilik Siyasəti</h2>
          <div className="flex items-center gap-2">
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline">Bax</a>
            <Button size="sm" onClick={() => saveSection('privacy_policy', privacy)} disabled={saving === 'privacy_policy'} className="bg-[#0077B6] hover:bg-[#005f92]">
              {saving === 'privacy_policy' ? '...' : saved === 'privacy_policy' ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
            </Button>
          </div>
        </div>
        <Textarea value={privacy} onChange={e => setPrivacy(e.target.value)} rows={15} className="font-mono text-sm" placeholder="# Məxfilik Siyasəti..." />
      </div>

      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#1A2530]">İstifadə Şərtləri</h2>
          <div className="flex items-center gap-2">
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] hover:underline">Bax</a>
            <Button size="sm" onClick={() => saveSection('terms_of_service', terms)} disabled={saving === 'terms_of_service'} className="bg-[#0077B6] hover:bg-[#005f92]">
              {saving === 'terms_of_service' ? '...' : saved === 'terms_of_service' ? 'Saxlanıldı!' : <><Save className="w-3 h-3 mr-1" /> Saxla</>}
            </Button>
          </div>
        </div>
        <Textarea value={terms} onChange={e => setTerms(e.target.value)} rows={15} className="font-mono text-sm" placeholder="# İstifadə Şərtləri..." />
      </div>
    </div>
  );
}




function MenuManager({ config, setConfig, builtInPages, iconLookup }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!config) return null;
  const items = config.items || [];

  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: 'menu_config', content: config }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };

  const updateItem = (i, field, val) => {
    const newItems = [...items]; newItems[i] = { ...newItems[i], [field]: val };
    setConfig({ ...config, items: newItems });
  };

  const moveItem = (i, dir) => {
    const newItems = [...items];
    const j = i + dir;
    if (j < 0 || j >= newItems.length) return;
    [newItems[i], newItems[j]] = [newItems[j], newItems[i]];
    newItems.forEach((m, idx) => m.order = idx);
    setConfig({ ...config, items: newItems });
  };

  const addItem = () => {
    const id = 'custom_' + Date.now();
    setConfig({ ...config, items: [...items, { id, label: 'Yeni Menyu', icon: 'Globe2', active: true, order: items.length, isCustom: true, slug: '' }] });
  };

  const removeItem = (i) => {
    if (!items[i].isCustom) { alert('Daxili menyular silinə bilməz, yalnız deaktiv edilə bilər.'); return; }
    const newItems = items.filter((_, idx) => idx !== i);
    newItems.forEach((m, idx) => m.order = idx);
    setConfig({ ...config, items: newItems });
  };

  const allIcons = ['Globe2','Users','FileText','Newspaper','MessageSquare','LayoutDashboard','Settings','HelpCircle','BarChart3','ClipboardList','TrendingUp','Cloud'];

  return (
    <div className="p-6 md:p-8 max-w-4xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">Menyu İdarəsi</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-2" /> Yeni Menyu</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
            {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Saxla</>}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item, i) => {
          const Icon = iconLookup[item.icon] || iconLookup.Globe2;
          const isBuiltIn = !!builtInPages[item.id];
          return (
            <div key={item.id} className={`bg-white rounded-xl p-4 border ${item.active !== false ? 'border-[#E2E8F0]' : 'border-red-200 bg-red-50/30'} flex items-center gap-4`}>
              {/* Order arrows */}
              <div className="flex flex-col gap-1">
                <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="w-7 h-7 rounded bg-[#F4F7F6] flex items-center justify-center hover:bg-[#E2E8F0] disabled:opacity-30">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                </button>
                <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} className="w-7 h-7 rounded bg-[#F4F7F6] flex items-center justify-center hover:bg-[#E2E8F0] disabled:opacity-30">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>

              {/* Icon */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.active !== false ? 'bg-[#0077B6]/10' : 'bg-red-100'}`}>
                <Icon className={`w-5 h-5 ${item.active !== false ? 'text-[#0077B6]' : 'text-red-400'}`} />
              </div>

              {/* Name */}
              <div className="flex-1">
                <Input value={item.label} onChange={e => updateItem(i, 'label', e.target.value)} className="h-8 font-semibold" />
              </div>

              {/* Icon selector */}
              <select value={item.icon} onChange={e => updateItem(i, 'icon', e.target.value)} className="h-8 rounded-md border border-input px-2 text-sm w-32">
                {allIcons.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>

              {/* Active toggle */}
              <button onClick={() => updateItem(i, 'active', item.active === false ? true : false)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${item.active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                {item.active !== false ? 'Aktiv' : 'Deaktiv'}
              </button>

              {/* Type badge */}
              <span className={`px-2 py-1 rounded text-[10px] font-bold ${isBuiltIn ? 'bg-[#0077B6]/10 text-[#0077B6]' : 'bg-amber-100 text-amber-700'}`}>
                {isBuiltIn ? 'Daxili' : 'Xüsusi'}
              </span>

              {/* URL/Slug for custom pages */}
              {item.isCustom && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-[#5C6B7A]">/</span>
                  <Input value={item.slug || ''} onChange={e => updateItem(i, 'slug', e.target.value.replace(/[^a-z0-9-]/g, ''))}
                    placeholder="url-slug" className="h-7 w-28 text-xs" />
                </div>
              )}

              {/* Delete (only custom) */}
              {item.isCustom && (
                <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-[#0077B6]/5 rounded-xl">
        <p className="text-xs text-[#0077B6]">
          <strong>Daxili menyular:</strong> Əsas Səhifə, Haqqımızda, Xidmətlər, Yeniliklər, Əlaqə, Header, Footer — deaktiv edilə bilər, silinə bilməz.<br/>
          <strong>Xüsusi menyular:</strong> "Yeni Menyu" ilə yaradılır — ad, ikon, aktiv/deaktiv, ardıcıllıq dəyişdirilə bilər, silinə bilər.<br/>
          <strong>Ardıcıllıq:</strong> ↑↓ oxlarla menyuların sırasını dəyişdirin.
        </p>
      </div>
    </div>
  );
}


function CustomPageEditor({ pageId, pageLabel }) {
  const [pg, setPg] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api('get', '/api/site-content').then(r => setPg(r.data[pageId] || {})).catch(console.error); }, [pageId]);
  const u = (f, v) => setPg(p => ({ ...p, [f]: v }));
  const save = async () => {
    setSaving(true);
    try { await api('put', '/api/site-content', { section: pageId, content: pg }); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch { alert('Xəta'); } finally { setSaving(false); }
  };

  const sections = pg.sections || [];
  const addSection = (type) => {
    const id = 's_' + Date.now();
    const templates = {
      text: { id, type: 'text', title: 'Yeni Bölmə', content: '' },
      cards: { id, type: 'cards', title: 'Kartlar', items: [{ title: '', desc: '', icon: 'BookOpen' }] },
      gallery: { id, type: 'gallery', title: 'Qaleriya', images: [] },
      cta: { id, type: 'cta', title: 'CTA Bölməsi', button_text: 'Əlaqə', button_url: '/contact', subtitle: '' },
      books: { id, type: 'books', title: 'Nəşrlərimiz', items: [{ title: '', author: '', desc: '', image: '', status: 'coming', price: '', url: '' }] },
    };
    setPg(p => ({ ...p, sections: [...(p.sections || []), templates[type] || templates.text] }));
  };
  const updateSection = (i, data) => { const s = [...sections]; s[i] = { ...s[i], ...data }; setPg(p => ({ ...p, sections: s })); };
  const removeSection = (i) => { const s = [...sections]; s.splice(i, 1); setPg(p => ({ ...p, sections: s })); };
  const moveSection = (i, dir) => { const s = [...sections]; const j = i + dir; if (j < 0 || j >= s.length) return; [s[i], s[j]] = [s[j], s[i]]; setPg(p => ({ ...p, sections: s })); };

  return (
    <div className="p-6 md:p-8 max-w-5xl overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1A2530]">{pageLabel} Səhifəsi Redaktoru</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6] hover:bg-[#005f92]">
          {saving ? '...' : saved ? 'Saxlanıldı!' : <><Save className="w-4 h-4 mr-2" /> Hamısını Saxla</>}
        </Button>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5">
        <h2 className="text-lg font-bold text-[#1A2530] mb-3">Hero Bölməsi</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Badge</label><Input value={pg.hero_badge||pageLabel} onChange={e=>u('hero_badge',e.target.value)} className="h-8" /></div>
          <div><label className="text-xs text-[#5C6B7A] mb-1 block">Başlıq</label><Input value={pg.hero_title||pageLabel} onChange={e=>u('hero_title',e.target.value)} className="h-8" /></div>
          <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={pg.hero_subtitle||''} onChange={e=>u('hero_subtitle',e.target.value)} className="h-8" /></div>
          <ImageUploadField label="Arxa fon şəkli" value={pg.hero_bg} onChange={v=>u('hero_bg',v)} />
        </div>
      </div>

      {/* Dynamic Sections */}
      {sections.map((sec, i) => (
        <div key={sec.id || i} className="bg-white rounded-xl p-5 border border-[#E2E8F0] mb-5 relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="w-6 h-6 rounded bg-[#F4F7F6] flex items-center justify-center disabled:opacity-30">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
              </button>
              <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="w-6 h-6 rounded bg-[#F4F7F6] flex items-center justify-center disabled:opacity-30">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              <span className="text-xs font-bold text-[#0077B6] uppercase">{sec.type === 'text' ? 'Mətn' : sec.type === 'cards' ? 'Kartlar' : sec.type === 'gallery' ? 'Qaleriya' : sec.type === 'books' ? 'Nəşrlər' : 'CTA'}</span>
            </div>
            <button onClick={() => removeSection(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>

          <Input value={sec.title || ''} onChange={e => updateSection(i, { title: e.target.value })} placeholder="Bölmə başlığı" className="h-8 mb-3 font-semibold" />

          {sec.type === 'text' && (
            <Textarea value={sec.content || ''} onChange={e => updateSection(i, { content: e.target.value })} rows={4} placeholder="Məzmun (Markdown dəstəklənir)" />
          )}

          {sec.type === 'cards' && (
            <div>
              <div className="grid grid-cols-3 gap-3">
                {(sec.items || []).map((card, ci) => (
                  <div key={ci} className="bg-[#F4F7F6] rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-1">
                      <select value={card.icon || 'BookOpen'} onChange={e => { const items = [...(sec.items||[])]; items[ci] = {...items[ci], icon: e.target.value}; updateSection(i, {items}); }} className="h-6 rounded border text-[10px] px-1 bg-white flex-1">
                        {['BookOpen','GraduationCap','Award','Target','Users','FileText','Globe2','Mountain','Layers','Video'].map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                      <button onClick={() => { const items = [...(sec.items||[])]; items.splice(ci,1); updateSection(i,{items}); }} className="text-red-400"><X className="w-3 h-3" /></button>
                    </div>
                    <Input value={card.title || ''} onChange={e => { const items = [...(sec.items||[])]; items[ci] = {...items[ci], title: e.target.value}; updateSection(i, {items}); }} placeholder="Başlıq" className="text-xs h-7" />
                    <Input value={card.desc || ''} onChange={e => { const items = [...(sec.items||[])]; items[ci] = {...items[ci], desc: e.target.value}; updateSection(i, {items}); }} placeholder="Təsvir" className="text-xs h-7" />
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="mt-2" onClick={() => { const items = [...(sec.items||[])]; items.push({title:'',desc:'',icon:'BookOpen'}); updateSection(i,{items}); }}>
                <Plus className="w-3 h-3 mr-1" /> Kart əlavə et
              </Button>
            </div>
          )}

          {sec.type === 'gallery' && (
            <div>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {(sec.images || []).map((img, gi) => (
                  <div key={gi} className="relative group">
                    {img && <img src={img} alt="" className="w-full h-20 rounded-lg object-cover border" />}
                    <button onClick={() => { const images = [...(sec.images||[])]; images.splice(gi,1); updateSection(i,{images}); }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">x</button>
                  </div>
                ))}
              </div>
              <ImageUploadField label="Şəkil əlavə et" value="" onChange={v => { if(v) updateSection(i, {images: [...(sec.images||[]), v]}); }} />
            </div>
          )}

          {sec.type === 'cta' && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#5C6B7A] mb-1 block">Alt yazı</label><Input value={sec.subtitle || ''} onChange={e => updateSection(i, { subtitle: e.target.value })} className="h-8" /></div>
              <div></div>
              <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə mətni</label><Input value={sec.button_text || 'Əlaqə'} onChange={e => updateSection(i, { button_text: e.target.value })} className="h-8" /></div>
              <div><label className="text-xs text-[#5C6B7A] mb-1 block">Düymə URL</label><Input value={sec.button_url || '/contact'} onChange={e => updateSection(i, { button_url: e.target.value })} className="h-8" /></div>
            </div>
          )}

          {sec.type === 'books' && (
            <div>
              <div className="space-y-4">
                {(sec.items || []).map((book, bi) => (
                  <div key={bi} className="bg-[#F4F7F6] rounded-xl p-4 border border-[#E2E8F0]">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-[#0077B6]">Kitab {bi+1}</span>
                      <button onClick={() => { const items = [...(sec.items||[])]; items.splice(bi,1); updateSection(i,{items}); }} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs text-[#5C6B7A] mb-1 block">Kitab adı</label><Input value={book.title||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],title:e.target.value};updateSection(i,{items});}} className="h-8" /></div>
                      <div><label className="text-xs text-[#5C6B7A] mb-1 block">Müəllif</label><Input value={book.author||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],author:e.target.value};updateSection(i,{items});}} className="h-8" /></div>
                      <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Qısa təsvir (kartda görünür)</label><Input value={book.short_desc||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],short_desc:e.target.value};updateSection(i,{items});}} className="h-8" placeholder="Kitab haqqında 1-2 cümlə" /></div>
                      <div className="col-span-2"><label className="text-xs text-[#5C6B7A] mb-1 block">Ətraflı təsvir (kitab səhifəsində, Markdown)</label><Textarea value={book.desc||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],desc:e.target.value};updateSection(i,{items});}} rows={2} /></div>
                      <ImageUploadField label="Üz qabığı şəkli" value={book.image} onChange={v=>{const items=[...(sec.items||[])];items[bi]={...items[bi],image:v};updateSection(i,{items});}} />
                      <div className="space-y-2">
                        <div><label className="text-xs text-[#5C6B7A] mb-1 block">Status</label>
                          <select value={book.status||'coming'} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],status:e.target.value};updateSection(i,{items});}} className="w-full h-8 rounded-md border border-input bg-background px-3 text-sm">
                            <option value="coming">Tezliklə</option><option value="available">Mövcuddur</option><option value="new">Yeni nəşr</option>
                          </select></div>
                        <div><label className="text-xs text-[#5C6B7A] mb-1 block">Qiymət</label><Input value={book.price||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],price:e.target.value};updateSection(i,{items});}} placeholder="15 AZN" className="h-8" /></div>
                        <div><label className="text-xs text-[#5C6B7A] mb-1 block">Sifariş/Ətraflı URL</label><Input value={book.url||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],url:e.target.value};updateSection(i,{items});}} placeholder="https://wa.me/..." className="h-8" /></div>
                        <div><label className="text-xs text-[#5C6B7A] mb-1 block">Səhifə slug (URL)</label><Input value={book.slug||''} onChange={e=>{const items=[...(sec.items||[])];items[bi]={...items[bi],slug:e.target.value.replace(/[^a-z0-9-]/g,'')};updateSection(i,{items});}} placeholder="map-scheme" className="h-8" /></div>
                      </div>
                      <div className="col-span-2 mt-2">
                        <label className="text-xs text-[#5C6B7A] mb-1 block">Kitabdan nümunə şəkillər ({(book.gallery||[]).length})</label>
                        <div className="flex gap-2 flex-wrap mb-2">
                          {(book.gallery||[]).map((img, gi) => (
                            <div key={gi} className="relative group">
                              <img src={img} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                              <button onClick={() => { const items=[...(sec.items||[])]; const g=[...(items[bi].gallery||[])]; g.splice(gi,1); items[bi]={...items[bi],gallery:g}; updateSection(i,{items}); }} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100">x</button>
                            </div>
                          ))}
                        </div>
                        <ImageUploadField label="Nümunə şəkil əlavə et" value="" onChange={v=>{ if(v){const items=[...(sec.items||[])]; items[bi]={...items[bi],gallery:[...(items[bi].gallery||[]),v]}; updateSection(i,{items});} }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => { const items = [...(sec.items||[])]; items.push({title:'',author:'',desc:'',image:'',status:'coming',price:'',url:''}); updateSection(i,{items}); }}>
                <Plus className="w-3 h-3 mr-1" /> Kitab əlavə et
              </Button>
            </div>
          )}
        </div>
      ))}

      {/* Add section buttons */}
      <div className="flex gap-3 flex-wrap">
        <Button variant="outline" onClick={() => addSection('text')}><Plus className="w-3 h-3 mr-1" /> Mətn Bölməsi</Button>
        <Button variant="outline" onClick={() => addSection('cards')}><Plus className="w-3 h-3 mr-1" /> Kartlar Bölməsi</Button>
        <Button variant="outline" onClick={() => addSection('gallery')}><Plus className="w-3 h-3 mr-1" /> Qaleriya Bölməsi</Button>
        <Button variant="outline" onClick={() => addSection('cta')}><Plus className="w-3 h-3 mr-1" /> CTA Bölməsi</Button>
        <Button variant="outline" onClick={() => addSection('books')} className="border-[#0077B6] text-[#0077B6]"><Plus className="w-3 h-3 mr-1" /> Nəşrlər/Kitablar</Button>
      </div>
    </div>
  );
}



function AdminAttemptCard({ attempt: a }) {
  const [expanded, setExpanded] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pct = a.total > 0 ? Math.round((a.score / a.total) * 100) : 0;
  const dateStr = (() => { const d=new Date(a.submitted_at); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })();
  const timeStr = a.time_spent > 0 ? `${Math.floor(a.time_spent / 60)} dəq ${a.time_spent % 60} san` : '';

  const loadPdfBlob = async () => {
    if (pdfBlobUrl) { setShowPdf(!showPdf); return; }
    try {
      const token = getToken();
      const resp = await fetch(`${API}/api/quiz-pdf/${a.quiz_id}?token=${token}`);
      if (resp.ok) { const blob = await resp.blob(); setPdfBlobUrl(URL.createObjectURL(blob)); setShowPdf(true); }
    } catch { alert('PDF yüklənmədi'); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid={`admin-attempt-${a.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 text-left">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-body font-semibold text-[#1A2530]">{a.student_name} — {a.quiz_title}</div>
            <div className="flex flex-wrap items-center gap-3 mt-1 font-body text-xs text-[#5C6B7A]">
              <span className="text-green-600 font-semibold">Düzgün: {a.correct}</span>
              <span className="text-red-600 font-semibold">Səhv: {a.wrong}</span>
              <span className="text-amber-600">Cərimə: -{a.penalty} ({a.penalty_rule} səhv = -{a.penalty_deduction || 1} bal)</span>
              {timeStr && <span>{timeStr}</span>}
              <span>{dateStr}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xl font-bold ${pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{a.score}/{a.total}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${pct >= 70 ? 'bg-green-100 text-green-700' : pct >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{pct}%</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-[#5C6B7A]" /> : <ChevronDown className="w-4 h-4 text-[#5C6B7A]" />}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-[#E2E8F0]">
          {a.quiz_type === 'pdf_test' && (
            <div className="p-3 border-b border-[#E2E8F0]">
              <Button size="sm" variant="outline" onClick={loadPdfBlob} className="text-xs mb-2">
                <FileText className="w-3.5 h-3.5 mr-1" /> {showPdf ? 'PDF-i gizlət' : 'PDF + Cavablar'}
              </Button>
              {showPdf && (
                <div className="flex h-[550px] border rounded-lg overflow-hidden">
                  {/* LEFT: PDF */}
                  <div className="flex-1 border-r border-[#E2E8F0]">
                    {pdfBlobUrl ? (
                      <iframe src={`${pdfBlobUrl}#toolbar=0&navpanes=0`} className="w-full h-full" title="PDF" />
                    ) : (
                      <div className="flex items-center justify-center h-full"><div className="w-6 h-6 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>
                    )}
                  </div>
                  {/* RIGHT: Answered Card */}
                  <div className="w-[300px] flex-shrink-0 overflow-y-auto p-3 bg-[#F8FAFB]">
                    <h4 className="text-xs font-bold text-[#1A2530] mb-2">Cavab Kartı</h4>
                    <div className="space-y-1.5">
                      {(a.results || []).map((r, i) => (
                        <div key={i} className={`flex items-center gap-1.5 p-2 rounded-lg text-xs ${r.is_open ? (r.is_correct === true ? 'bg-green-50 border border-green-200' : r.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200') : r.is_correct ? 'bg-green-50 border border-green-200' : r.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-white border flex-shrink-0">{i + 1}</span>
                          {r.is_open ? (
                            r.is_correct === true ? (
                              <><CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /><span className="text-green-700 font-semibold truncate flex-1">{r.user_answer}</span></>
                            ) : r.is_correct === false ? (
                              <><XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" /><span className="text-red-700 truncate">{r.user_answer || '-'}</span><span className="text-green-600 ml-auto font-bold text-[10px] truncate max-w-[80px]" title={r.correct_answer}>{r.correct_answer}</span></>
                            ) : (
                              <span className="text-blue-700 truncate flex-1" title={r.user_answer}>{r.user_answer || '-'}</span>
                            )
                          ) : r.is_correct ? (
                            <><CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /><span className="text-green-700 font-semibold">{r.user_answer}</span></>
                          ) : (
                            <><XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" /><span className="text-red-700">{r.user_answer || '-'}</span><span className="text-green-600 ml-auto font-bold">{r.correct_answer}</span></>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Answer details (always shown for non-PDF or as summary) */}
          {a.quiz_type !== 'pdf_test' && (
          <div className="p-4">
            <h4 className="text-sm font-bold text-[#1A2530] mb-2">Cavablar</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {(a.results || []).map((r, i) => (
                <div key={i} className={`flex items-center gap-1.5 p-2 rounded-lg text-xs ${r.is_open ? (r.is_correct === true ? 'bg-green-50 border border-green-200' : r.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200') : r.is_correct ? 'bg-green-50 border border-green-200' : r.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <span className="font-bold w-5">{i + 1}.</span>
                  {r.is_open ? (
                    r.is_correct === true ? (
                      <><CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /><span className="text-green-700">{r.user_answer}</span></>
                    ) : r.is_correct === false ? (
                      <><XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" /><span className="text-red-700">{r.user_answer || '-'}</span><span className="text-green-600 ml-auto font-semibold">{r.correct_answer}</span></>
                    ) : (
                      <span className="text-blue-700 truncate flex-1" title={r.user_answer}>{r.user_answer || '-'}</span>
                    )
                  ) : r.is_correct ? (
                    <><CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /><span className="text-green-700">{r.user_answer}</span></>
                  ) : (
                    <><XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" /><span className="text-red-700">{r.user_answer || '-'}</span><span className="text-green-600 ml-auto font-semibold">{r.correct_answer}</span></>
                  )}
                </div>
              ))}
            </div>
          </div>
          )}
          {/* Penalty info - always shown */}
          <div className="px-4 pb-4">
            <div className="p-2 bg-[#F4F7F6] rounded-lg text-xs text-[#5C6B7A] flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" />
              Düzgün: {a.correct} | Səhv: {a.wrong} | Qayda: {a.penalty_rule} səhv = -{a.penalty_deduction || 1} bal |
              Cərimə: {a.wrong > 0 && a.penalty_rule > 0 ? `${a.wrong}÷${a.penalty_rule}=${Math.floor(a.wrong / a.penalty_rule)}×${a.penalty_deduction || 1}=` : ''}-{a.penalty} |
              Yekun: {a.correct}-{a.penalty}={a.score}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function azSort(a, b) {
  const az = 'AaBbCcÇçDdEeƏəFfGgĞğHhXxIıİiJjKkQqLlMmNnOoÖöPpRrSsŞşTtUuÜüVvYyZz';
  const ia = az.indexOf(a) >= 0 ? az.indexOf(a) : 999;
  const ib = az.indexOf(b) >= 0 ? az.indexOf(b) : 999;
  return ia - ib;
}
function azCompare(a, b) {
  const min = Math.min(a.length, b.length);
  for (let i = 0; i < min; i++) {
    const d = azSort(a[i], b[i]);
    if (d !== 0) return d;
  }
  return a.length - b.length;
}
function naturalCompare(a, b) {
  const pa = a.match(/(\d+|\D+)/g) || [];
  const pb = b.match(/(\d+|\D+)/g) || [];
  for (let i = 0; i < Math.min(pa.length, pb.length); i++) {
    const na = Number(pa[i]), nb = Number(pb[i]);
    if (!isNaN(na) && !isNaN(nb)) {
      if (na !== nb) return na - nb;
    } else {
      const d = azCompare(pa[i], pb[i]);
      if (d !== 0) return d;
    }
  }
  return pa.length - pb.length;
}

function StudentsTab({ students, groups, setEditItem, setShowDialog, setDeleteConfirm, fetchAll, prepCategory }) {
  const [studentSort, setStudentSort] = useState('name-az');
  const [groupSort, setGroupSort] = useState('name-az');
  const [groupFilter, setGroupFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [trashItems, setTrashItems] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [selectedTrash, setSelectedTrash] = useState([]);

  const fetchTrash = useCallback(async () => {
    try { const { data } = await api('get', '/api/trash'); setTrashItems(data); }
    catch { setTrashItems([]); }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash, students.length, groups.length]);

  const groupMap = {};
  groups.forEach(g => { groupMap[g.id] = g.name; });

  const filteredStudents = students.filter(s => {
    if (groupFilter === 'none') return !s.group_id;
    if (groupFilter !== 'all' && s.group_id !== groupFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    if (studentSort === 'name-az') return naturalCompare(a.name || '', b.name || '');
    if (studentSort === 'name-za') return naturalCompare(b.name || '', a.name || '');
    if (studentSort === 'group') return naturalCompare(groupMap[a.group_id] || '', groupMap[b.group_id] || '');
    return 0;
  });

  const filteredGroups = groupSearchQuery.trim()
    ? groups.filter(g => (g.name || '').toLowerCase().includes(groupSearchQuery.trim().toLowerCase()))
    : groups;

  const sortedGroups = [...filteredGroups].sort((a, b) => {
    if (groupSort === 'name-az') return naturalCompare(a.name || '', b.name || '');
    if (groupSort === 'name-za') return naturalCompare(b.name || '', a.name || '');
    if (groupSort === 'members') return students.filter(s => s.group_id === b.id).length - students.filter(s => s.group_id === a.id).length;
    return 0;
  });

  const hasActiveFilter = groupFilter !== 'all' || searchQuery.trim();

  const toggleStudent = (id) => setSelectedStudents(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllStudents = () => {
    const ids = sortedStudents.map(s => s.id);
    setSelectedStudents(p => p.length === ids.length ? [] : ids);
  };
  const toggleGroup = (id) => setSelectedGroups(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllGroups = () => {
    const ids = sortedGroups.map(g => g.id);
    setSelectedGroups(p => p.length === ids.length ? [] : ids);
  };
  const toggleTrash = (id) => setSelectedTrash(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleBulkDeleteStudents = async () => {
    if (!selectedStudents.length) return;
    try { await api('post', '/api/students/bulk-delete', { ids: selectedStudents }); setSelectedStudents([]); fetchAll(); fetchTrash(); }
    catch { alert('Toplu silmə xətası'); }
  };
  const handleBulkDeleteGroups = async () => {
    if (!selectedGroups.length) return;
    try { await api('post', '/api/groups/bulk-delete', { ids: selectedGroups }); setSelectedGroups([]); fetchAll(); fetchTrash(); }
    catch { alert('Toplu silmə xətası'); }
  };

  const handleTrashAction = async (action, id) => {
    try {
      if (action === 'restore') await api('post', `/api/trash/restore/${id}`);
      else await api('delete', `/api/trash/${id}`);
      fetchAll(); fetchTrash();
    } catch { alert('Xəta baş verdi'); }
  };

  const handleBulkTrashAction = async (action) => {
    if (!selectedTrash.length) return;
    try {
      if (action === 'restore') await api('post', '/api/trash/bulk-restore', { ids: selectedTrash });
      else await api('post', '/api/trash/bulk-delete', { ids: selectedTrash });
      setSelectedTrash([]); fetchAll(); fetchTrash();
    } catch { alert('Xəta baş verdi'); }
  };

  const bin1 = trashItems.filter(t => t.bin_level === 1);
  const bin2 = trashItems.filter(t => t.bin_level === 2);
  const personTerm = prepCategory === 'abituriyent' ? 'Şagird' : 'Tələbə';
  const personTermPlural = prepCategory === 'abituriyent' ? 'Şagirdlər' : 'Tələbələr';
  const cc = CATEGORY_COLORS[prepCategory] || CATEGORY_COLORS.abituriyent;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <h1 className="text-3xl font-bold text-[#1A2530] mb-6">{personTermPlural} & Qruplar</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT: Students */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#1A2530]">{personTermPlural} ({hasActiveFilter ? `${filteredStudents.length} / ${students.length}` : students.length})</h2>
            <div className="flex gap-2">
              {selectedStudents.length > 0 && (
                <Button size="sm" variant="destructive" onClick={handleBulkDeleteStudents} data-testid="bulk-delete-students" className="h-8 text-xs">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> {selectedStudents.length} seçilmişi sil
                </Button>
              )}
              <a href={`${API}/api/students/export`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-xs text-[#5C6B7A] hover:bg-[#F4F7F6]" data-testid="students-export-btn">Export</a>
              <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-xs text-[#5C6B7A] hover:bg-[#F4F7F6] cursor-pointer" data-testid="students-import-btn">
                Import
                <input type="file" accept=".csv,.xlsx" className="sr-only" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const formData = new FormData(); formData.append('file', file);
                  try { const { data } = await api('post', '/api/students/import', formData); alert(`${data.imported} ${personTerm.toLowerCase()} import olundu`); fetchAll(); }
                  catch { alert('Import xətası'); } e.target.value = '';
                }} />
              </label>
              <Button size="sm" onClick={() => { setEditItem(null); setShowDialog('student'); }} className={`${cc.activeBg} ${cc.hoverBg} h-8`} data-testid="add-student-btn">
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Yeni
              </Button>
            </div>
          </div>
          {/* Search */}
          <div className="mb-3">
            <Input placeholder="Ad və ya e-poçt ilə axtar..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="search-students"
              className="h-9 text-sm bg-white border-[#E2E8F0] focus:border-[#0077B6] focus:ring-[#0077B6]/20" />
          </div>
          {/* Sort + Filter */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <button onClick={toggleAllStudents} data-testid="select-all-students"
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-white text-[#5C6B7A] border border-[#E2E8F0] hover:bg-[#F4F7F6] flex items-center gap-1">
              {selectedStudents.length === sortedStudents.length && sortedStudents.length > 0 ? <CheckSquare className="w-3.5 h-3.5" style={{color: cc.primary}} /> : <Square className="w-3.5 h-3.5" />} Hamısı
            </button>
            <span className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
            {[{id:'name-az',l:'Ad A→Z'},{id:'name-za',l:'Ad Z→A'},{id:'group',l:'Sinif'}].map(s => (
              <button key={s.id} onClick={() => setStudentSort(s.id)} data-testid={`sort-students-${s.id}`}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${studentSort === s.id ? `${cc.activeBg} text-white` : 'bg-white text-[#5C6B7A] border border-[#E2E8F0] hover:bg-[#F4F7F6]'}`}>{s.l}</button>
            ))}
            <span className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
            <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} data-testid="filter-students-group"
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer outline-none ${groupFilter !== 'all' ? `${cc.activeBg} text-white` : 'bg-white text-[#5C6B7A] border border-[#E2E8F0] hover:bg-[#F4F7F6]'}`}>
              <option value="all">Bütün siniflər</option>
              {[...groups].sort((a,b) => naturalCompare(a.name||'', b.name||'')).map(g => <option key={g.id} value={g.id}>{g.name} ({students.filter(s => s.group_id === g.id).length})</option>)}
              <option value="none">Qrupsuz</option>
            </select>
            {hasActiveFilter && (
              <button onClick={() => { setGroupFilter('all'); setSearchQuery(''); }} data-testid="clear-group-filter"
                className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-all flex items-center gap-1">
                <X className="w-3 h-3" /> Sıfırla
              </button>
            )}
          </div>
          {/* 2-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sortedStudents.map(s => (
              <div key={s.id} className={`bg-white rounded-xl p-3 border flex items-center gap-2 ${selectedStudents.includes(s.id) ? `border-2` : 'border-[#E2E8F0]'}`} style={selectedStudents.includes(s.id) ? {borderColor: cc.primary, backgroundColor: cc.primary + '08'} : {}} data-testid={`student-row-${s.id}`}>
                <button onClick={() => toggleStudent(s.id)} className="flex-shrink-0" data-testid={`check-student-${s.id}`}>
                  {selectedStudents.includes(s.id) ? <CheckSquare className="w-4 h-4" style={{color: cc.primary}} /> : <Square className="w-4 h-4 text-[#CBD5E1]" />}
                </button>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: cc.primary + '15'}}><Users className="w-3.5 h-3.5" style={{color: cc.primary}} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#1A2530] truncate">{s.name}</div>
                  <div className="text-xs text-[#5C6B7A] truncate">{s.email} {s.group_id ? `| ${groupMap[s.group_id] || ''}` : ''}</div>
                </div>
                <div className="flex gap-0.5 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditItem(s); setShowDialog('student'); }}><Edit className="w-3.5 h-3.5 text-[#5C6B7A]" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDeleteConfirm({type:'students',id:s.id,name:s.name})}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              </div>
            ))}
          </div>
          {filteredStudents.length === 0 && <p className="text-center py-8 text-sm text-[#5C6B7A]">{hasActiveFilter ? 'Nəticə tapılmadı' : `Hələ ${personTerm.toLowerCase()} yoxdur`}</p>}
        </div>

        {/* RIGHT: Groups */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#1A2530]">Qruplar ({groupSearchQuery.trim() ? `${sortedGroups.length} / ${groups.length}` : groups.length})</h2>
            <div className="flex gap-2">
              {selectedGroups.length > 0 && (
                <Button size="sm" variant="destructive" onClick={handleBulkDeleteGroups} data-testid="bulk-delete-groups" className="h-8 text-xs">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> {selectedGroups.length} sil
                </Button>
              )}
              <a href={`${API}/api/groups/export`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-xs text-[#5C6B7A] hover:bg-[#F4F7F6]" data-testid="groups-export-btn">Export</a>
              <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-xs text-[#5C6B7A] hover:bg-[#F4F7F6] cursor-pointer" data-testid="groups-import-btn">
                Import
                <input type="file" accept=".csv,.xlsx" className="sr-only" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const formData = new FormData(); formData.append('file', file);
                  try { const { data } = await api('post', '/api/groups/import', formData); alert(`${data.imported} qrup import olundu`); fetchAll(); }
                  catch { alert('Import xətası'); } e.target.value = '';
                }} />
              </label>
              <Button size="sm" onClick={() => { setEditItem(null); setShowDialog('group'); }} className={`${cc.activeBg} ${cc.hoverBg} h-8`}>
                <FolderOpen className="w-3.5 h-3.5 mr-1" /> Yeni
              </Button>
            </div>
          </div>
          {/* Search */}
          <div className="mb-3">
            <Input placeholder="Qrup adı ilə axtar..." value={groupSearchQuery} onChange={e => setGroupSearchQuery(e.target.value)} data-testid="search-groups"
              className="h-9 text-sm bg-white border-[#E2E8F0] focus:border-[#03045E] focus:ring-[#03045E]/20" />
          </div>
          {/* Sort */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <button onClick={toggleAllGroups} data-testid="select-all-groups"
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-white text-[#5C6B7A] border border-[#E2E8F0] hover:bg-[#F4F7F6] flex items-center gap-1">
              {selectedGroups.length === sortedGroups.length && sortedGroups.length > 0 ? <CheckSquare className="w-3.5 h-3.5" style={{color: cc.primary}} /> : <Square className="w-3.5 h-3.5" />} Hamısı
            </button>
            <span className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
            {[{id:'name-az',l:'Ad A→Z'},{id:'name-za',l:'Ad Z→A'},{id:'members',l:`${personTerm} sayı`}].map(s => (
              <button key={s.id} onClick={() => setGroupSort(s.id)} data-testid={`sort-groups-${s.id}`}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${groupSort === s.id ? `${cc.activeBg} text-white` : 'bg-white text-[#5C6B7A] border border-[#E2E8F0] hover:bg-[#F4F7F6]'}`}>{s.l}</button>
            ))}
          </div>
          <div className="space-y-2">
            {sortedGroups.map(g => {
              const memberCount = students.filter(s => s.group_id === g.id).length;
              return (
                <div key={g.id} className={`bg-white rounded-xl p-4 border ${selectedGroups.includes(g.id) ? 'border-2' : 'border-[#E2E8F0]'}`} style={selectedGroups.includes(g.id) ? {borderColor: cc.primary, backgroundColor: cc.primary + '08'} : {}} data-testid={`group-row-${g.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleGroup(g.id)} className="flex-shrink-0" data-testid={`check-group-${g.id}`}>
                        {selectedGroups.includes(g.id) ? <CheckSquare className="w-4 h-4" style={{color: cc.primary}} /> : <Square className="w-4 h-4 text-[#CBD5E1]" />}
                      </button>
                      <FolderOpen className="w-5 h-5 text-amber-500" />
                      <div>
                        <div className="font-semibold text-sm text-[#1A2530]">{g.name}</div>
                        <div className="text-xs text-[#5C6B7A]">{memberCount} {personTerm.toLowerCase()}</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-group-${g.id}`} onClick={() => { setEditItem(g); setShowDialog('group'); }}><Edit className="w-3.5 h-3.5 text-[#5C6B7A]" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`delete-group-${g.id}`} onClick={() => setDeleteConfirm({type:'groups',id:g.id,name:g.name})}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
            {sortedGroups.length === 0 && <p className="text-center py-8 text-sm text-[#5C6B7A]">{groupSearchQuery.trim() ? 'Nəticə tapılmadı' : 'Hələ qrup yoxdur'}</p>}
          </div>
        </div>
      </div>

      {/* TRASH / RECYCLE BIN */}
      <div className="mt-8 border-t border-[#E2E8F0] pt-6">
        <button onClick={() => setShowTrash(!showTrash)} data-testid="toggle-trash" className="flex items-center gap-2 text-lg font-bold text-[#1A2530] hover:text-[#0077B6] transition-colors">
          <Trash2 className="w-5 h-5" /> Zibil qutusu ({trashItems.length})
          {showTrash ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showTrash && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 space-y-6">
            {/* Bulk actions */}
            {selectedTrash.length > 0 && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-[#5C6B7A] font-semibold">{selectedTrash.length} seçilmiş:</span>
                <Button size="sm" onClick={() => handleBulkTrashAction('restore')} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="bulk-restore-trash">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Geri qaytar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleBulkTrashAction('delete')} className="h-8 text-xs" data-testid="bulk-delete-trash">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                </Button>
              </div>
            )}

            {/* BIN 1 */}
            <div>
              <h3 className="text-sm font-bold text-[#1A2530] mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500" /> Zibil qutusu 1 ({bin1.length})
                <span className="text-xs font-normal text-[#5C6B7A]">— Geri qaytarıla bilər</span>
              </h3>
              {bin1.length === 0 && <p className="text-xs text-[#5C6B7A] pl-4">Boşdur</p>}
              <div className="space-y-1.5">
                {bin1.map(item => (
                  <div key={item.id} className={`flex items-center gap-2 p-2.5 rounded-lg border ${selectedTrash.includes(item.id) ? 'border-amber-400 bg-amber-50' : 'border-[#E2E8F0] bg-white'}`} data-testid={`trash-item-${item.id}`}>
                    <button onClick={() => toggleTrash(item.id)} className="flex-shrink-0">
                      {selectedTrash.includes(item.id) ? <CheckSquare className="w-4 h-4 text-amber-600" /> : <Square className="w-4 h-4 text-[#CBD5E1]" />}
                    </button>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${item.type === 'student' ? 'bg-[#0077B6]/10' : 'bg-amber-100'}`}>
                      {item.type === 'student' ? <Users className="w-3 h-3 text-[#0077B6]" /> : <FolderOpen className="w-3 h-3 text-amber-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-[#1A2530] truncate">{item.data?.name || item.data?.email || '?'}</span>
                      <span className="text-xs text-[#5C6B7A] ml-2">{item.type === 'student' ? 'Şagird' : 'Qrup'}</span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-testid={`restore-${item.id}`}
                        onClick={() => handleTrashAction('restore', item.id)}><RotateCcw className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`trash-delete-${item.id}`}
                        onClick={() => handleTrashAction('delete', item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* BIN 2 */}
            <div>
              <h3 className="text-sm font-bold text-[#1A2530] mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500" /> Zibil qutusu 2 ({bin2.length})
                <span className="text-xs font-normal text-[#5C6B7A]">— Son şans! Buradan silsəniz geri dönmür</span>
              </h3>
              {bin2.length === 0 && <p className="text-xs text-[#5C6B7A] pl-4">Boşdur</p>}
              <div className="space-y-1.5">
                {bin2.map(item => (
                  <div key={item.id} className={`flex items-center gap-2 p-2.5 rounded-lg border ${selectedTrash.includes(item.id) ? 'border-red-400 bg-red-50' : 'border-red-200 bg-red-50/30'}`} data-testid={`trash-item-${item.id}`}>
                    <button onClick={() => toggleTrash(item.id)} className="flex-shrink-0">
                      {selectedTrash.includes(item.id) ? <CheckSquare className="w-4 h-4 text-red-600" /> : <Square className="w-4 h-4 text-[#CBD5E1]" />}
                    </button>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${item.type === 'student' ? 'bg-[#0077B6]/10' : 'bg-amber-100'}`}>
                      {item.type === 'student' ? <Users className="w-3 h-3 text-[#0077B6]" /> : <FolderOpen className="w-3 h-3 text-amber-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-[#1A2530] truncate">{item.data?.name || item.data?.email || '?'}</span>
                      <span className="text-xs text-[#5C6B7A] ml-2">{item.type === 'student' ? 'Şagird' : 'Qrup'}</span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" data-testid={`restore-${item.id}`}
                        onClick={() => handleTrashAction('restore', item.id)}><RotateCcw className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`trash-delete-${item.id}`}
                        onClick={() => handleTrashAction('delete', item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

